import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { useCompatRouter } from '../hooks/useCompatRouter'
import { updateUnits, updateGlobalSeasons, type SeasonalPeriod } from '../store/slices/roomsSlice'
import { addBooking, updateBooking, updateBookingStatus, deleteBooking, updateBookings, type Booking } from '../store/slices/bookingsSlice'
import { addGuest, updateGuest, deleteGuest, updateGuestBookingStats, updateGuests, type Guest } from '../store/slices/guestsSlice'
import { updateUser } from '../store/slices/authSlice'
import { defaultHomepageSettings, setHomepageSettings, updateHomepageSettings, updateUnitDescription, updateFeatureCard } from '../store/slices/homepageSlice'
import { updatePortalProfile } from '../services/guestPortalSupabase'
import { getApiUrl, getPlaceholderImageUrl } from '../utils/api'
import BookingCalendar from '../components/BookingCalendar'

// Define types for business settings
interface BusinessSettings {
  name: string
  address: string
  phone: string
  email: string
  website: string
  description: string
  logo: string
}

// Define types for data items
interface SeasonRate {
  id: string
  seasonName: string
  dateRange: string
  highSeasonRate: number
  midSeasonRate: number
  lowSeasonRate: number
  minStay: number
  adminFee: number
  breakageDeposit: number
}

interface Unit {
  id: string
  number: string
  roomNumbers?: string[]
  type: string
  features: string
  status: string
  highSeasonRate: number
  midSeasonRate: number
  lowSeasonRate: number
  minStay?: number
  adminFee?: number
  breakageDeposit?: number
  images?: string[]
  heroImageIndex?: number
  seasonRates?: SeasonRate[]
  color?: string
  availableRooms?: number
  totalRooms?: number
}

interface User {
  id: string
  firstName: string
  lastName: string
  email: string
  role: string
  status: string
  lastLogin: string
}

interface RegisteredGuestAccount {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  createdAt: string
  totalBookings: number
  totalSpent: number
  lastVisit: string | null
  hasGuestProfile: boolean
}

interface RegisteredGuestMarketingEmailForm {
  subject: string
  message: string
}

interface RegisteredGuestMarketingAttachment {
  fileName: string
  mimeType: string
  base64Content: string
  sizeBytes: number
}

const MAX_REGISTERED_GUEST_MARKETING_ATTACHMENT_BYTES = 5 * 1024 * 1024

interface CalendarRoomUnit {
  id: number
  roomNumber: string
  roomTypeId: number
  roomType: string
  status: string
}

interface RoomLockoutRecord {
  id: string
  roomId: number
  roomNumber: string
  roomTypeId: number
  roomType: string
  startDate: string
  endDate: string
  reason: string | null
  createdAt: string
  createdBy: string | null
}

interface AvailabilityConflictRecord {
  id: string
  loggedAt: string | null
  changedAt: string | null
  source: string
  bookingReference: string
  paymentReference: string | null
  roomTypeId: number | null
  roomType: string | null
  checkIn: string | null
  checkOut: string | null
  guestEmail: string | null
  totalAmount: number | null
  followUpRequired: boolean
}

interface PaymentGatewaySettings {
  yocoPublicKey: string
  yocoSecretKey: string
  yocoWebhookSecret: string
  eftBankName: string
  eftAccountName: string
  eftAccountNumber: string
  eftBranchCode: string
  eftAccountType: string
  eftReferencePrefix: string
  eftInstructions: string
}

interface AdminAuthSettings {
  adminEmail: string
  provider: string
  credentialStorage: string
  passwordManagementSupported: boolean
}

interface GuestFeedbackModerationItem {
  id: string
  sourceId: string
  sourceType: 'booking' | 'enquiry' | 'website'
  guestName: string
  guestContact: string | null
  location: string
  stayLabel: string
  quote: string
  sourceLabel: string
  submittedAt: string
  isApproved: boolean
  isFeatured: boolean
}

const defaultBusinessSettings: BusinessSettings = {
  name: 'Marina Glen Holiday Resort',
  address: '123 Coastal Drive, Port Alfred, 6170',
  phone: '+27 46 624 1234',
  email: 'info@marinaglen.com',
  website: 'https://marinaglen.com',
  description: 'Experience luxury coastal accommodation at Marina Glen Holiday Resort. Our premium units offer breathtaking ocean views and world-class amenities for the perfect getaway.',
  logo: '/logo.png'
}

const defaultBookingPageSettings = {
  title: 'Book Your Stay at Marina Glen',
  subtitle: 'Experience luxury coastal accommodation',
  primaryColor: '#3B82F6',
  accentColor: '#06B6D4',
  backgroundColor: '#F8FAFC',
  headerBackgroundColor: '#FFFFFF',
  textColor: '#1F2937',
  buttonColor: '#3B82F6',
  buttonTextColor: '#FFFFFF',
  showUnitImages: true,
  showUnitFeatures: true,
  showPricing: true,
  showGuestReviews: true,
  contactEmail: 'bookings@marinaglen.com',
  contactPhone: '+27 46 624 1234',
  cancellationPolicy: 'Free cancellation up to 48 hours before check-in',
  checkInTime: '15:00',
  checkOutTime: '11:00',
  welcomeMessage: 'Welcome to Marina Glen Holiday Resort! We look forward to hosting you.',
  footerText: '© 2025 Marina Glen Holiday Resort. All rights reserved.'
}

const defaultPaymentGatewaySettings: PaymentGatewaySettings = {
  yocoPublicKey: '',
  yocoSecretKey: '',
  yocoWebhookSecret: '',
  eftBankName: 'Standard Bank',
  eftAccountName: 'Marina Glen Holiday Resort',
  eftAccountNumber: '084629451',
  eftBranchCode: '051001',
  eftAccountType: 'Business Cheque',
  eftReferencePrefix: 'MG',
  eftInstructions: 'Use your booking reference when making payment and upload proof of payment.'
}

export default function AdminDashboard() {
  // Navigation
  const router = useCompatRouter()
  
  // Redux integration
  const dispatch = useAppDispatch()
  const { units: reduxUnits, globalSeasons } = useAppSelector((state) => state.rooms)
  const { bookings: reduxBookings } = useAppSelector((state) => state.bookings)
  const { guests: reduxGuests } = useAppSelector((state) => state.guests)
  const { settings: homepageSettings } = useAppSelector((state) => state.homepage)
  const authUser = useAppSelector((state) => state.auth.user)
  const authToken = useAppSelector((state) => state.auth.token)
  
  const [bookingsLoading, setBookingsLoading] = useState(false)
  const [bookingsInitialized, setBookingsInitialized] = useState(false)
  const [registeredGuests, setRegisteredGuests] = useState<RegisteredGuestAccount[]>([])
  const [registeredGuestsLoading, setRegisteredGuestsLoading] = useState(false)
  const [registeredGuestsInitialized, setRegisteredGuestsInitialized] = useState(false)
  const [registeredGuestsMessage, setRegisteredGuestsMessage] = useState('')
  const [showRegisteredGuestMarketingPanel, setShowRegisteredGuestMarketingPanel] = useState(false)
  const [registeredGuestMarketingSending, setRegisteredGuestMarketingSending] = useState(false)
  const [registeredGuestMarketingAttachmentLoading, setRegisteredGuestMarketingAttachmentLoading] = useState(false)
  const [registeredGuestMarketingForm, setRegisteredGuestMarketingForm] = useState<RegisteredGuestMarketingEmailForm>({
    subject: '',
    message: ''
  })
  const [registeredGuestMarketingAttachment, setRegisteredGuestMarketingAttachment] = useState<RegisteredGuestMarketingAttachment | null>(null)
  const [resetPasswordInfo, setResetPasswordInfo] = useState<{ email: string; temporaryPassword: string } | null>(null)
  const [availabilityConflicts, setAvailabilityConflicts] = useState<AvailabilityConflictRecord[]>([])
  const [availabilityConflictsLoading, setAvailabilityConflictsLoading] = useState(false)
  const [availabilityConflictsMessage, setAvailabilityConflictsMessage] = useState('')
  const [bookingCalendarView, setBookingCalendarView] = useState<'calendar' | 'lockouts'>('calendar')
  const [calendarRoomUnits, setCalendarRoomUnits] = useState<CalendarRoomUnit[]>([])
  const [calendarRoomUnitsLoading, setCalendarRoomUnitsLoading] = useState(false)
  const [roomLockouts, setRoomLockouts] = useState<RoomLockoutRecord[]>([])
  const [roomLockoutsLoading, setRoomLockoutsLoading] = useState(false)
  const [roomLockoutsSaving, setRoomLockoutsSaving] = useState(false)
  const [roomLockoutsMessage, setRoomLockoutsMessage] = useState('')
  const [roomLockoutForm, setRoomLockoutForm] = useState({
    roomId: '',
    startDate: '',
    endDate: '',
    reason: ''
  })

  const fetchBookings = async (resetBeforeLoad = false) => {
    setBookingsLoading(true)
    if (resetBeforeLoad) {
      dispatch(updateBookings([]))
      dispatch(updateGuests([]))
    }
    try {
      const res = await fetch(`${getApiUrl()}/api/bookings`, { headers: getAuthHeaders() })
      if (!res.ok) return
      const json = await res.json()
      if (!json.success || !Array.isArray(json.data)) return
      const mapped: Booking[] = json.data.map((row: Record<string, unknown>) => ({
        id: String(row.booking_reference ?? row.id ?? ''),
        guestName: String(row.guest_name ?? ''),
        guestEmail: String(row.guest_email ?? ''),
        guestPhone: String(row.guest_phone ?? ''),
        unitType: String(row.unit_type ?? ''),
        unitNumber: String(row.room_number ?? row.room_id ?? ''),
        checkIn: String(row.check_in ?? '').split('T')[0],
        checkOut: String(row.check_out ?? '').split('T')[0],
        guests: String(row.guests_count ?? ''),
        totalAmount: Number(row.total_amount ?? 0),
        status: row.status
          ? String(row.status).charAt(0).toUpperCase() + String(row.status).slice(1)
          : 'Pending',
        payment: String(row.payment_status ?? 'pending'),
        nights: Number(row.nights ?? 0),
        baseRate: Number(row.base_rate ?? 0),
        adminFee: Number(row.admin_fee ?? 0),
        breakageDeposit: Number(row.breakage_deposit ?? 0),
        seasonApplied: row.season_applied ? String(row.season_applied) : undefined,
      }))
      dispatch(updateBookings(mapped))

      // Derive unique guests from bookings and populate Guest Management
      const guestMap = new Map<string, Guest>()
      for (const booking of mapped) {
        if (!booking.guestEmail) continue
        const existing = guestMap.get(booking.guestEmail)
        if (existing) {
          existing.totalBookings += 1
          existing.totalSpent += booking.totalAmount
          if (booking.checkOut > existing.lastVisit) {
            existing.lastVisit = booking.checkOut
          }
        } else {
          guestMap.set(booking.guestEmail, {
            id: `guest-${booking.guestEmail}`,
            name: booking.guestName,
            email: booking.guestEmail,
            phone: booking.guestPhone || '',
            totalBookings: 1,
            totalSpent: booking.totalAmount,
            lastVisit: booking.checkOut,
            status: 'Active',
            preferences: '',
            notes: '',
            createdAt: booking.checkIn,
          })
        }
      }
      dispatch(updateGuests(Array.from(guestMap.values())))
    } catch (err) {
      console.error('Failed to fetch bookings from API:', err)
    } finally {
      setBookingsLoading(false)
      setBookingsInitialized(true)
    }
  }

  // Fetch bookings on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void fetchBookings(true) }, [])

  // ── Season Settings ─────────────────────────────────────────────────────────
  const [seasonsLoading, setSeasonsLoading] = useState(false)
  const [seasonForm, setSeasonForm] = useState({ start_date: '', end_date: '', season: 'high' as 'high' | 'mid' | 'low' })
  const [seasonFormError, setSeasonFormError] = useState('')
  const [seasonSaving, setSeasonSaving] = useState(false)

  const fetchSeasons = async () => {
    setSeasonsLoading(true)
    try {
      const res = await fetch(`${getApiUrl()}/api/seasons`)
      const json = await res.json()
      if (!json.success || !Array.isArray(json.data)) return
      const mapped: SeasonalPeriod[] = json.data.map((row: Record<string, unknown>) => ({
        id: String(row.id),
        unitType: '',
        startDate: String(row.start_date ?? '').split('T')[0],
        endDate: String(row.end_date ?? '').split('T')[0],
        season: row.season as 'low' | 'mid' | 'high',
      }))
      dispatch(updateGlobalSeasons(mapped))
    } catch (err) {
      console.error('Failed to fetch seasons:', err)
    } finally {
      setSeasonsLoading(false)
    }
  }

  const handleAddSeason = async () => {
    setSeasonFormError('')
    if (!seasonForm.start_date || !seasonForm.end_date) {
      setSeasonFormError('Start date and end date are required.')
      return
    }
    if (seasonForm.start_date >= seasonForm.end_date) {
      setSeasonFormError('End date must be after start date.')
      return
    }
    setSeasonSaving(true)
    try {
      const res = await fetch(`${getApiUrl()}/api/seasons`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(seasonForm),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to save')
      setSeasonForm({ start_date: '', end_date: '', season: 'high' })
      await fetchSeasons()
    } catch (err: unknown) {
      setSeasonFormError(err instanceof Error ? err.message : 'Failed to save season.')
    } finally {
      setSeasonSaving(false)
    }
  }

  const handleDeleteSeason = async (id: string) => {
    if (!window.confirm('Delete this season period?')) return
    try {
      await fetch(`${getApiUrl()}/api/seasons/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
      await fetchSeasons()
    } catch (err) {
      console.error('Failed to delete season:', err)
    }
  }

  // Fetch seasons on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void fetchSeasons() }, [])

  const [unitsLoading, setUnitsLoading] = useState(true)
  const [unitsInitialized, setUnitsInitialized] = useState(false)

  const fetchUnits = async (resetBeforeLoad = false) => {
    setUnitsLoading(true)
    if (resetBeforeLoad) {
      updateUnitsInStore([])
    }
    try {
      const res = await fetch(`${getApiUrl()}/api/rooms`)
      const json = await res.json()
      if (!json.success || !Array.isArray(json.data)) return
      const mapped: Unit[] = json.data.map((r: Record<string, any>) => {
        const savedRoomNumber = String(r.room_number || '').trim()
        const savedUnitType = String(r.type || r.name || '').trim()

        return {
          id: String(r.id),
          number: savedRoomNumber || 'Unit number not set',
          roomNumbers: Array.isArray(r.room_numbers)
            ? r.room_numbers.map((value: unknown) => String(value || '').trim()).filter(Boolean)
            : [],
          type: savedUnitType,
          features: r.description || (Array.isArray(r.amenities) ? r.amenities.join(', ') : ''),
          status: String(r.status || '').toLowerCase() === 'maintenance'
            ? 'Maintenance'
            : String(r.status || '').toLowerCase() === 'blocked'
              ? 'Maintenance'
              : String(r.status || '').toLowerCase() === 'occupied'
                ? 'Occupied'
                : 'Available',
          highSeasonRate: r.pricing?.high_season ?? 0,
          midSeasonRate: r.pricing?.mid_season ?? 0,
          lowSeasonRate: r.pricing?.low_season ?? 0,
          minStay: r.pricing?.min_stay ?? 1,
          adminFee: r.pricing?.admin_fee ?? 0,
          breakageDeposit: r.pricing?.breakage_deposit ?? 0,
          images: Array.isArray(r.images) ? r.images : [],
          heroImageIndex: Number(r.hero_image_index ?? 0),
          seasonRates: [],
          color: r.color || 'blue',
          availableRooms: Number(r.available_rooms ?? 0),
          totalRooms: Number(r.total_rooms ?? 0),
        }
      })
      updateUnitsInStore(mapped)
    } catch (err) {
      console.error('Failed to fetch units from API:', err)
    } finally {
      setUnitsLoading(false)
      setUnitsInitialized(true)
    }
  }

  // Fetch units on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void fetchUnits(true) }, [])

  // Load registered guest accounts on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void fetchRegisteredGuests(true) }, [])

  // Navigation state
  const [activeTab, setActiveTab] = useState('dashboard')
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'delete'>('view')
  const [modalType, setModalType] = useState<string>('unit')
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [formData, setFormData] = useState<any>({})

  // Use Redux state as primary source, fallback to local state
  const [units, setUnits] = useState<Unit[]>([])
  const [users, setUsers] = useState<User[]>([])
  const visibleBookings = bookingsInitialized ? reduxBookings : []
  const activeBookings = visibleBookings.filter((booking) => String(booking.status || '').toLowerCase() !== 'cancelled')
  const visibleRegisteredGuests = registeredGuestsInitialized ? registeredGuests : []

  // Sync local state with Redux state
  useEffect(() => {
    if (!unitsInitialized) {
      return
    }

    setUnits(reduxUnits)
  }, [reduxUnits, unitsInitialized])

  // Update Redux store when units change
  const updateUnitsInStore = (newUnits: Unit[]) => {
    setUnits(newUnits)
    dispatch(updateUnits(newUnits))
  }

  const [businessSettings, setBusinessSettings] = useState<BusinessSettings>(defaultBusinessSettings)
  const [businessSettingsLoading, setBusinessSettingsLoading] = useState(false)
  const [businessSettingsSaving, setBusinessSettingsSaving] = useState(false)
  const [businessSettingsMessage, setBusinessSettingsMessage] = useState('')
  const [homepageSettingsSaving, setHomepageSettingsSaving] = useState(false)
  const [homepageSettingsMessage, setHomepageSettingsMessage] = useState('')

  // Season rates state - REMOVED
  // const [seasonRates, setSeasonRates] = useState([
  //   { id: 1, name: 'Peak Season', dateRange: 'Dec 15 - Jan 15', rate: '+25%', color: 'blue' },
  //   { id: 2, name: 'High Season', dateRange: 'Nov 1 - Dec 14, Jan 16 - Mar 31', rate: '+15%', color: 'green' },
  //   { id: 3, name: 'Standard Season', dateRange: 'Apr 1 - Oct 31', rate: 'Base Rate', color: 'gray' }
  // ])

  // Booking page settings state
  const [bookingPageSettings, setBookingPageSettings] = useState(defaultBookingPageSettings)
  const [bookingPageSettingsLoading, setBookingPageSettingsLoading] = useState(false)
  const [bookingPageSettingsSaving, setBookingPageSettingsSaving] = useState(false)
  const [bookingPageSettingsMessage, setBookingPageSettingsMessage] = useState('')
  const [guestFeedbackItems, setGuestFeedbackItems] = useState<GuestFeedbackModerationItem[]>([])
  const [guestFeedbackLoading, setGuestFeedbackLoading] = useState(false)
  const [guestFeedbackSaving, setGuestFeedbackSaving] = useState(false)
  const [guestFeedbackMessage, setGuestFeedbackMessage] = useState('')
  const [paymentGatewaySettings, setPaymentGatewaySettings] = useState<PaymentGatewaySettings>(defaultPaymentGatewaySettings)
  const [paymentSettingsLoading, setPaymentSettingsLoading] = useState(false)
  const [paymentSettingsSaving, setPaymentSettingsSaving] = useState(false)
  const [paymentSettingsMessage, setPaymentSettingsMessage] = useState('')
  const [secureSaveAvailable, setSecureSaveAvailable] = useState(false)
  const [adminProfileSaving, setAdminProfileSaving] = useState(false)
  const [adminAuthSettings, setAdminAuthSettings] = useState<AdminAuthSettings>({
    adminEmail: '',
    provider: 'supabase',
    credentialStorage: 'supabase-auth',
    passwordManagementSupported: false,
  })
  const [adminAuthLoading, setAdminAuthLoading] = useState(false)
  const [adminAuthMessage, setAdminAuthMessage] = useState('')

  const getStoredToken = () => {
    return authToken || ''
  }

  const getAuthHeaders = () => {
    const token = getStoredToken()

    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  }

  const isCurrentAdminProfileModal = modalType === 'user' && Boolean(formData.isCurrentAdminProfile)
  const adminDisplayName = [authUser?.firstName, authUser?.lastName].filter(Boolean).join(' ').trim() || 'Admin User'
  const adminInitial = (authUser?.firstName || authUser?.email || 'A').charAt(0).toUpperCase()

  const openCurrentAdminProfileModal = () => {
    openModal('edit', {
      firstName: authUser?.firstName || '',
      lastName: authUser?.lastName || '',
      email: authUser?.email || adminAuthSettings.adminEmail || '',
      role: authUser?.role || 'admin',
      isCurrentAdminProfile: true,
      modalType: 'user',
    })
  }

  const saveCurrentAdminProfile = async () => {
    const firstName = String(formData.firstName || '').trim()
    const lastName = String(formData.lastName || '').trim()

    if (!firstName) {
      alert('First name is required.')
      return
    }

    setAdminProfileSaving(true)

    try {
      const updatedUser = await updatePortalProfile(firstName, lastName)

      dispatch(updateUser({
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        email: updatedUser.email,
        role: updatedUser.role,
      }))

      closeModal()
      alert('Profile updated successfully!')
    } catch (error: any) {
      alert(error?.message || 'Failed to update your profile.')
    } finally {
      setAdminProfileSaving(false)
    }
  }

  const openBookingPreview = async (bookingId: string) => {
    const loadingPreviewUrl = new URL(
      '/booking-system/booking-preview?previewLoading=1',
      window.location.origin,
    ).toString()
    const previewWindow = window.open(loadingPreviewUrl, '_blank')

    if (!previewWindow) {
      alert('Unable to open the booking preview. Please allow pop-ups for this site and try again.')
      return
    }

    try {
      const response = await fetch(`${getApiUrl()}/api/bookings/public-access`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ bookingId }),
      })

      const result = await response.json().catch(() => null)
      if (!response.ok || !result?.success || !result?.data?.token) {
        previewWindow?.close()
        alert(result?.error || 'Failed to open the booking preview.')
        return
      }

      const previewUrl = new URL(
        `/booking-system/booking-preview?token=${encodeURIComponent(result.data.token)}`,
        window.location.origin,
      ).toString()

      if (previewWindow) {
        previewWindow.location.replace(previewUrl)
        return
      }

      window.open(previewUrl, '_blank')
    } catch (error) {
      previewWindow?.close()
      console.error('Failed to open booking preview:', error)
      alert('Failed to open the booking preview.')
    }
  }

  const fetchCalendarRoomUnits = async () => {
    setCalendarRoomUnitsLoading(true)

    try {
      const response = await fetch(`${getApiUrl()}/api/admin/calendar/room-units`, {
        headers: getAuthHeaders()
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to load room units')
      }

      const units: CalendarRoomUnit[] = Array.isArray(result?.data)
        ? result.data.map((unit: Record<string, unknown>) => ({
            id: Number(unit.id ?? 0),
            roomNumber: String(unit.roomNumber ?? ''),
            roomTypeId: Number(unit.roomTypeId ?? 0),
            roomType: String(unit.roomType ?? ''),
            status: String(unit.status ?? 'available'),
          }))
        : []

      setCalendarRoomUnits(units)
    } catch (error: any) {
      setRoomLockoutsMessage(error?.message || 'Failed to load room units.')
    } finally {
      setCalendarRoomUnitsLoading(false)
    }
  }

  const fetchRoomLockouts = async () => {
    setRoomLockoutsLoading(true)

    try {
      const response = await fetch(`${getApiUrl()}/api/admin/calendar/lockouts`, {
        headers: getAuthHeaders()
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to load room lockouts')
      }

      const lockouts: RoomLockoutRecord[] = Array.isArray(result?.data)
        ? result.data.map((lockout: Record<string, unknown>) => ({
            id: String(lockout.id ?? ''),
            roomId: Number(lockout.roomId ?? 0),
            roomNumber: String(lockout.roomNumber ?? ''),
            roomTypeId: Number(lockout.roomTypeId ?? 0),
            roomType: String(lockout.roomType ?? ''),
            startDate: String(lockout.startDate ?? ''),
            endDate: String(lockout.endDate ?? ''),
            reason: lockout.reason ? String(lockout.reason) : null,
            createdAt: String(lockout.createdAt ?? ''),
            createdBy: lockout.createdBy ? String(lockout.createdBy) : null,
          }))
        : []

      setRoomLockouts(lockouts)
    } catch (error: any) {
      setRoomLockoutsMessage(error?.message || 'Failed to load room lockouts.')
    } finally {
      setRoomLockoutsLoading(false)
    }
  }

  const handleCreateRoomLockout = async () => {
    if (!roomLockoutForm.roomId) {
      setRoomLockoutsMessage('Please select a unit for the lockout.')
      return
    }

    if (!roomLockoutForm.startDate || !roomLockoutForm.endDate) {
      setRoomLockoutsMessage('Start date and end date are required.')
      return
    }

    if (roomLockoutForm.startDate >= roomLockoutForm.endDate) {
      setRoomLockoutsMessage('End date must be after start date.')
      return
    }

    setRoomLockoutsSaving(true)
    setRoomLockoutsMessage('')

    try {
      const response = await fetch(`${getApiUrl()}/api/admin/calendar/lockouts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          roomId: Number(roomLockoutForm.roomId),
          startDate: roomLockoutForm.startDate,
          endDate: roomLockoutForm.endDate,
          reason: roomLockoutForm.reason,
        }),
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to save lockout')
      }

      setRoomLockoutsMessage(result?.message || 'Lockout saved successfully.')
      setRoomLockoutForm({ roomId: '', startDate: '', endDate: '', reason: '' })
      await fetchRoomLockouts()
      await fetchBookings()
    } catch (error: any) {
      setRoomLockoutsMessage(error?.message || 'Failed to save room lockout.')
    } finally {
      setRoomLockoutsSaving(false)
    }
  }

  const handleDeleteRoomLockout = async (lockout: RoomLockoutRecord) => {
    if (!window.confirm(`Remove the lockout for ${lockout.roomNumber} from ${lockout.startDate} to ${lockout.endDate}?`)) {
      return
    }

    setRoomLockoutsMessage('')

    try {
      const response = await fetch(`${getApiUrl()}/api/admin/calendar/lockouts/${lockout.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to remove lockout')
      }

      setRoomLockoutsMessage(result?.message || 'Lockout removed successfully.')
      await fetchRoomLockouts()
      await fetchBookings()
    } catch (error: any) {
      setRoomLockoutsMessage(error?.message || 'Failed to remove room lockout.')
    }
  }

  const fetchRegisteredGuests = async (resetBeforeLoad = false) => {
    setRegisteredGuestsLoading(true)
    setRegisteredGuestsMessage('')
    if (resetBeforeLoad) {
      setRegisteredGuests([])
    }

    try {
      const response = await fetch(`${getApiUrl()}/api/admin/registered-guests`, {
        headers: getAuthHeaders()
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to load registered guests')
      }

      const guestAccounts: RegisteredGuestAccount[] = Array.isArray(result?.data)
        ? result.data.map((guest: Record<string, unknown>) => ({
            id: String(guest.id ?? ''),
            firstName: String(guest.firstName ?? ''),
            lastName: String(guest.lastName ?? ''),
            email: String(guest.email ?? ''),
            phone: String(guest.phone ?? ''),
            createdAt: String(guest.createdAt ?? ''),
            totalBookings: Number(guest.totalBookings ?? 0),
            totalSpent: Number(guest.totalSpent ?? 0),
            lastVisit: guest.lastVisit ? String(guest.lastVisit) : null,
            hasGuestProfile: Boolean(guest.hasGuestProfile),
          }))
        : []

      setRegisteredGuests(guestAccounts)
    } catch (error: any) {
      setRegisteredGuestsMessage(error?.message || 'Failed to load registered guests.')
    } finally {
      setRegisteredGuestsLoading(false)
      setRegisteredGuestsInitialized(true)
    }
  }

  const fetchAvailabilityConflicts = async () => {
    setAvailabilityConflictsLoading(true)
    setAvailabilityConflictsMessage('')

    try {
      const response = await fetch(`${getApiUrl()}/api/admin/availability-conflicts`, {
        headers: getAuthHeaders()
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to load availability conflicts')
      }

      const conflicts: AvailabilityConflictRecord[] = Array.isArray(result?.data)
        ? result.data.map((item: Record<string, unknown>) => ({
            id: String(item.id ?? ''),
            loggedAt: item.loggedAt ? String(item.loggedAt) : null,
            changedAt: item.changedAt ? String(item.changedAt) : null,
            source: String(item.source ?? 'unknown'),
            bookingReference: String(item.bookingReference ?? ''),
            paymentReference: item.paymentReference ? String(item.paymentReference) : null,
            roomTypeId: item.roomTypeId == null || item.roomTypeId === '' ? null : Number(item.roomTypeId),
            roomType: item.roomType ? String(item.roomType) : null,
            checkIn: item.checkIn ? String(item.checkIn) : null,
            checkOut: item.checkOut ? String(item.checkOut) : null,
            guestEmail: item.guestEmail ? String(item.guestEmail) : null,
            totalAmount: item.totalAmount == null || item.totalAmount === '' ? null : Number(item.totalAmount),
            followUpRequired: Boolean(item.followUpRequired),
          }))
        : []

      setAvailabilityConflicts(conflicts)
    } catch (error: any) {
      setAvailabilityConflictsMessage(error?.message || 'Failed to load availability conflicts.')
    } finally {
      setAvailabilityConflictsLoading(false)
    }
  }

  const handleResetRegisteredGuestPassword = async (guest: RegisteredGuestAccount) => {
    if (!window.confirm(`Reset the password for ${guest.email}? A temporary password will be generated.`)) {
      return
    }

    setRegisteredGuestsMessage('')
    setResetPasswordInfo(null)

    try {
      const response = await fetch(`${getApiUrl()}/api/admin/registered-guests/${guest.id}/reset-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to reset guest password')
      }

      setResetPasswordInfo({
        email: String(result?.data?.email || guest.email),
        temporaryPassword: String(result?.data?.temporaryPassword || ''),
      })
      setRegisteredGuestsMessage(`Password reset for ${guest.email}. Share the temporary password securely.`)
    } catch (error: any) {
      setRegisteredGuestsMessage(error?.message || 'Failed to reset guest password.')
    }
  }

  const handleDeleteRegisteredGuest = async (guest: RegisteredGuestAccount) => {
    if (!window.confirm(`Remove the guest profile for ${guest.email}? This deletes the login account but keeps historical bookings.`)) {
      return
    }

    setRegisteredGuestsMessage('')
    setResetPasswordInfo(null)

    try {
      const response = await fetch(`${getApiUrl()}/api/admin/registered-guests/${guest.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to remove guest profile')
      }

      setRegisteredGuestsMessage(result?.message || `Removed ${guest.email}.`)
      await fetchRegisteredGuests()
    } catch (error: any) {
      setRegisteredGuestsMessage(error?.message || 'Failed to remove guest profile.')
    }
  }

  const handleSendRegisteredGuestMarketingEmail = async () => {
    const subject = registeredGuestMarketingForm.subject.trim()
    const message = registeredGuestMarketingForm.message.trim()

    if (!subject) {
      setRegisteredGuestsMessage('Marketing email subject is required.')
      return
    }

    if (!message) {
      setRegisteredGuestsMessage('Marketing email message is required.')
      return
    }

    if (registeredGuests.length === 0) {
      setRegisteredGuestsMessage('There are no registered guest accounts to email.')
      return
    }

    setRegisteredGuestMarketingSending(true)
    setRegisteredGuestsMessage('')
    setResetPasswordInfo(null)

    try {
      const response = await fetch(`${getApiUrl()}/api/admin/registered-guests/marketing-email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          subject,
          message,
          attachment: registeredGuestMarketingAttachment,
          guestIds: registeredGuests.map((guest) => guest.id),
        }),
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to send marketing email')
      }

      setRegisteredGuestsMessage(result?.message || `Marketing email sent to ${registeredGuests.length} registered guests.`)
      setRegisteredGuestMarketingForm({ subject: '', message: '' })
      setRegisteredGuestMarketingAttachment(null)
      setShowRegisteredGuestMarketingPanel(false)
    } catch (error: any) {
      setRegisteredGuestsMessage(error?.message || 'Failed to send marketing email.')
    } finally {
      setRegisteredGuestMarketingSending(false)
    }
  }

  const handleRegisteredGuestMarketingAttachmentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    event.target.value = ''

    if (!selectedFile) {
      return
    }

    if (selectedFile.size > MAX_REGISTERED_GUEST_MARKETING_ATTACHMENT_BYTES) {
      setRegisteredGuestsMessage('Attachment must be 5 MB or smaller.')
      return
    }

    setRegisteredGuestMarketingAttachmentLoading(true)
    setRegisteredGuestsMessage('')

    try {
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => {
          const result = typeof reader.result === 'string' ? reader.result : ''
          const base64Segment = result.includes(',') ? result.split(',')[1] : result
          if (!base64Segment) {
            reject(new Error('Could not read the selected attachment.'))
            return
          }

          resolve(base64Segment)
        }

        reader.onerror = () => reject(new Error('Could not read the selected attachment.'))
        reader.readAsDataURL(selectedFile)
      })

      setRegisteredGuestMarketingAttachment({
        fileName: selectedFile.name,
        mimeType: selectedFile.type || 'application/octet-stream',
        base64Content,
        sizeBytes: selectedFile.size,
      })
    } catch (error: any) {
      setRegisteredGuestsMessage(error?.message || 'Failed to prepare the attachment.')
    } finally {
      setRegisteredGuestMarketingAttachmentLoading(false)
    }
  }

  const loadPaymentGatewaySettings = async () => {
    setPaymentSettingsLoading(true)
    setPaymentSettingsMessage('')
    setSecureSaveAvailable(false)

    try {
      const token = getStoredToken()

      if (!token) {
        setPaymentSettingsMessage('Secure payment settings require an authenticated admin session.')
        return
      }

      const response = await fetch(`${getApiUrl()}/api/admin/payment-settings`, {
        headers: getAuthHeaders()
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Unable to load payment settings')
      }

      setPaymentGatewaySettings({
        ...defaultPaymentGatewaySettings,
        ...(result?.data || {})
      })
      setPaymentSettingsMessage('Payment settings loaded from Supabase.')
      setSecureSaveAvailable(true)
    } catch (error) {
      console.error('Failed to load payment gateway settings:', error)
      setPaymentSettingsMessage('Failed to load payment settings.')
      setSecureSaveAvailable(false)
    } finally {
      setPaymentSettingsLoading(false)
    }
  }

  const loadBusinessSettings = async () => {
    setBusinessSettingsLoading(true)
    setBusinessSettingsMessage('')

    try {
      const response = await fetch(`${getApiUrl()}/api/business-settings`)
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to load business settings')
      }

      setBusinessSettings({
        ...defaultBusinessSettings,
        ...(result?.data || {})
      })
    } catch (error: any) {
      setBusinessSettingsMessage(error?.message || 'Failed to load business settings.')
    } finally {
      setBusinessSettingsLoading(false)
    }
  }

  const loadHomepageSettings = async () => {
    setHomepageSettingsMessage('')

    try {
      const response = await fetch(`${getApiUrl()}/api/homepage-settings`)
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to load homepage settings')
      }

      if (result?.data) {
        dispatch(setHomepageSettings(result.data))
      }
    } catch (error: any) {
      setHomepageSettingsMessage(error?.message || 'Failed to load homepage settings.')
    }
  }

  const persistBusinessSettings = async (settingsToSave: BusinessSettings, successMessage: string) => {
    setBusinessSettingsSaving(true)
    setBusinessSettingsMessage('')

    try {
      const token = getStoredToken()
      if (!token) {
        setBusinessSettingsMessage('Secure save unavailable: please sign in with an authenticated admin account.')
        return false
      }

      const response = await fetch(`${getApiUrl()}/api/business-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settingsToSave)
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to save business settings')
      }

      setBusinessSettings({
        ...defaultBusinessSettings,
        ...(result?.data || {})
      })
      setBusinessSettingsMessage(successMessage)
      return true
    } catch (error: any) {
      setBusinessSettingsMessage(error?.message || 'Failed to save business settings.')
      return false
    } finally {
      setBusinessSettingsSaving(false)
    }
  }

  const loadBookingPageSettings = async () => {
    setBookingPageSettingsLoading(true)
    setBookingPageSettingsMessage('')

    try {
      const response = await fetch(`${getApiUrl()}/api/booking-page-settings`)
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to load booking page settings')
      }

      setBookingPageSettings({
        ...defaultBookingPageSettings,
        ...(result?.data || {})
      })
    } catch (error: any) {
      setBookingPageSettingsMessage(error?.message || 'Failed to load booking page settings.')
    } finally {
      setBookingPageSettingsLoading(false)
    }
  }

  const persistBookingPageSettings = async (settingsToSave: typeof defaultBookingPageSettings, successMessage: string) => {
    setBookingPageSettingsSaving(true)
    setBookingPageSettingsMessage('')

    try {
      const token = getStoredToken()
      if (!token) {
        setBookingPageSettingsMessage('Secure save unavailable: please sign in with an authenticated admin account.')
        return false
      }

      const response = await fetch(`${getApiUrl()}/api/booking-page-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settingsToSave)
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to save booking page settings')
      }

      setBookingPageSettings({
        ...defaultBookingPageSettings,
        ...(result?.data || {})
      })
      setBookingPageSettingsMessage(successMessage)
      return true
    } catch (error: any) {
      setBookingPageSettingsMessage(error?.message || 'Failed to save booking page settings.')
      return false
    } finally {
      setBookingPageSettingsSaving(false)
    }
  }

  const loadGuestFeedbackModeration = async () => {
    setGuestFeedbackLoading(true)
    setGuestFeedbackMessage('')

    try {
      const token = getStoredToken()
      if (!token) {
        setGuestFeedbackItems([])
        setGuestFeedbackMessage('Sign in as admin to moderate public guest feedback.')
        return
      }

      const response = await fetch(`${getApiUrl()}/api/admin/guest-feedback`, {
        headers: getAuthHeaders()
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to load guest feedback moderation items')
      }

      const items: GuestFeedbackModerationItem[] = Array.isArray(result?.data)
        ? result.data.map((item: Record<string, unknown>) => ({
            id: String(item.id ?? ''),
            sourceId: String(item.sourceId ?? ''),
            sourceType: item.sourceType === 'booking' ? 'booking' : item.sourceType === 'enquiry' ? 'enquiry' : 'website',
            guestName: String(item.guestName ?? 'Guest'),
            guestContact: typeof item.guestContact === 'string' && item.guestContact.trim() ? item.guestContact.trim() : null,
            location: String(item.location ?? 'Marina Glen guest'),
            stayLabel: String(item.stayLabel ?? 'Marina Glen stay'),
            quote: String(item.quote ?? ''),
            sourceLabel: String(item.sourceLabel ?? 'Guest feedback'),
            submittedAt: String(item.submittedAt ?? ''),
            isApproved: Boolean(item.isApproved),
            isFeatured: Boolean(item.isFeatured),
          }))
        : []

      setGuestFeedbackItems(items)
    } catch (error: any) {
      setGuestFeedbackMessage(error?.message || 'Failed to load guest feedback moderation items.')
    } finally {
      setGuestFeedbackLoading(false)
    }
  }

  const persistGuestFeedbackModeration = async () => {
    setGuestFeedbackSaving(true)
    setGuestFeedbackMessage('')

    try {
      const token = getStoredToken()
      if (!token) {
        setGuestFeedbackMessage('Sign in as admin to save guest feedback moderation.')
        return false
      }

      const response = await fetch(`${getApiUrl()}/api/admin/guest-feedback`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          entries: guestFeedbackItems.map((item) => ({
            sourceId: item.sourceId,
            sourceType: item.sourceType,
            isApproved: item.isApproved,
            isFeatured: item.isApproved ? item.isFeatured : false,
          }))
        })
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to save guest feedback moderation')
      }

      const items: GuestFeedbackModerationItem[] = Array.isArray(result?.data)
        ? result.data.map((item: Record<string, unknown>) => ({
            id: String(item.id ?? ''),
            sourceId: String(item.sourceId ?? ''),
            sourceType: item.sourceType === 'booking' ? 'booking' : item.sourceType === 'enquiry' ? 'enquiry' : 'website',
            guestName: String(item.guestName ?? 'Guest'),
            guestContact: typeof item.guestContact === 'string' && item.guestContact.trim() ? item.guestContact.trim() : null,
            location: String(item.location ?? 'Marina Glen guest'),
            stayLabel: String(item.stayLabel ?? 'Marina Glen stay'),
            quote: String(item.quote ?? ''),
            sourceLabel: String(item.sourceLabel ?? 'Guest feedback'),
            submittedAt: String(item.submittedAt ?? ''),
            isApproved: Boolean(item.isApproved),
            isFeatured: Boolean(item.isFeatured),
          }))
        : []

      setGuestFeedbackItems(items)
      setGuestFeedbackMessage('Guest feedback moderation saved successfully.')
      return true
    } catch (error: any) {
      setGuestFeedbackMessage(error?.message || 'Failed to save guest feedback moderation.')
      return false
    } finally {
      setGuestFeedbackSaving(false)
    }
  }

  const updateGuestFeedbackItem = (
    itemId: string,
    field: 'isApproved' | 'isFeatured',
    nextValue: boolean
  ) => {
    setGuestFeedbackItems((current) => current.map((item) => {
      if (item.id !== itemId) {
        return item
      }

      if (field === 'isApproved') {
        return {
          ...item,
          isApproved: nextValue,
          isFeatured: nextValue ? item.isFeatured : false,
        }
      }

      return {
        ...item,
        isApproved: nextValue ? true : item.isApproved,
        isFeatured: nextValue,
      }
    }))
  }

  const removeGuestFeedbackItem = async (item: GuestFeedbackModerationItem) => {
    if (!window.confirm(`Remove feedback from ${item.guestName}? This will clear it from the moderation list.`)) {
      return
    }

    setGuestFeedbackSaving(true)
    setGuestFeedbackMessage('')

    try {
      const token = getStoredToken()
      if (!token) {
        setGuestFeedbackMessage('Sign in as admin to remove guest feedback.')
        return
      }

      const query = new URLSearchParams({
        sourceId: item.sourceId,
        sourceType: item.sourceType,
      })
      const response = await fetch(`${getApiUrl()}/api/admin/guest-feedback?${query.toString()}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to remove guest feedback')
      }

      const items: GuestFeedbackModerationItem[] = Array.isArray(result?.data)
        ? result.data.map((entry: Record<string, unknown>) => ({
            id: String(entry.id ?? ''),
            sourceId: String(entry.sourceId ?? ''),
            sourceType: entry.sourceType === 'booking' ? 'booking' : entry.sourceType === 'enquiry' ? 'enquiry' : 'website',
            guestName: String(entry.guestName ?? 'Guest'),
            guestContact: typeof entry.guestContact === 'string' && entry.guestContact.trim() ? entry.guestContact.trim() : null,
            location: String(entry.location ?? 'Marina Glen guest'),
            stayLabel: String(entry.stayLabel ?? 'Marina Glen stay'),
            quote: String(entry.quote ?? ''),
            sourceLabel: String(entry.sourceLabel ?? 'Guest feedback'),
            submittedAt: String(entry.submittedAt ?? ''),
            isApproved: Boolean(entry.isApproved),
            isFeatured: Boolean(entry.isFeatured),
          }))
        : []

      setGuestFeedbackItems(items)
      setGuestFeedbackMessage(result?.message || 'Guest feedback removed successfully.')
    } catch (error: any) {
      setGuestFeedbackMessage(error?.message || 'Failed to remove guest feedback.')
    } finally {
      setGuestFeedbackSaving(false)
    }
  }

  const savePaymentGatewaySettings = async () => {
    if (!window.confirm('Save payment gateway settings?')) {
      return
    }

    setPaymentSettingsSaving(true)
    setPaymentSettingsMessage('')
    try {
      const token = getStoredToken()
      if (!token) {
        setSecureSaveAvailable(false)
        setPaymentSettingsMessage('Secure save unavailable: please sign in with an authenticated admin account.')
        return
      }

      const response = await fetch(`${getApiUrl()}/api/admin/payment-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(paymentGatewaySettings)
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to save payment settings')
      }

      setPaymentGatewaySettings({
        ...defaultPaymentGatewaySettings,
        ...(result?.data || {})
      })
      setSecureSaveAvailable(true)
      setPaymentSettingsMessage('Payment settings saved successfully.')
    } catch (error: any) {
      console.error('Failed to save payment gateway settings:', error)
      setPaymentSettingsMessage(error?.message || 'Failed to save payment settings.')
      setSecureSaveAvailable(false)
    } finally {
      setPaymentSettingsSaving(false)
    }
  }

  const loadAdminAuthSettings = async () => {
    setAdminAuthLoading(true)
    setAdminAuthMessage('')

    try {
      const token = getStoredToken()
      if (!token) {
        setAdminAuthMessage('Sign in as admin to load admin access settings.')
        return
      }

      const response = await fetch(`${getApiUrl()}/api/admin-auth-settings`, {
        headers: getAuthHeaders()
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to load admin access settings')
      }

      setAdminAuthSettings((prev) => ({
        ...prev,
        adminEmail: result?.data?.adminEmail || prev.adminEmail,
        provider: result?.data?.provider || prev.provider,
        credentialStorage: result?.data?.credentialStorage || prev.credentialStorage,
        passwordManagementSupported: Boolean(result?.data?.passwordManagementSupported),
      }))
      setAdminAuthMessage('Admin access is managed through Supabase Auth.')
    } catch (error: any) {
      setAdminAuthMessage(error?.message || 'Failed to load admin access settings.')
    } finally {
      setAdminAuthLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'bookingpage') {
      void loadBookingPageSettings()
      void loadGuestFeedbackModeration()
      void loadPaymentGatewaySettings()
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab === 'settings') {
      void loadBusinessSettings()
      void loadAdminAuthSettings()
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab === 'homepage') {
      void loadHomepageSettings()
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab === 'availability-conflicts') {
      void fetchAvailabilityConflicts()
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab === 'calendar') {
      void fetchCalendarRoomUnits()
      void fetchRoomLockouts()
    }
  }, [activeTab])

  // Modal functions
  const openModal = (mode: 'view' | 'edit' | 'delete', item: any = null) => {
    const modalType = item?.modalType || 'unit'
    const modalItem = item ? { ...item } : null
    if (modalItem && 'modalType' in modalItem) {
      delete modalItem.modalType
    }
    setModalMode(mode)
    setModalType(modalType)
    setSelectedItem(modalItem)
    setFormData(modalItem || {})
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedItem(null)
    setFormData({})
  }

  const handleSave = () => {
    alert('Changes saved successfully!')
    closeModal()
  }

  const saveUnitToApi = async () => {
    const label = (formData.number || formData.type || '4 Sleeper Unit').trim()
    const typeLabel = (formData.type || formData.number || '4 Sleeper Unit').trim()
    const capacity = typeLabel.includes('8') ? 8 : typeLabel.includes('6') ? 6 : typeLabel.includes('4') ? 4 : 2
    const featureText = String(formData.features || '').trim()
    const normalizedStatus = String(formData.status || '').toLowerCase() === 'occupied'
      ? 'occupied'
      : String(formData.status || '').toLowerCase() === 'maintenance'
        ? 'maintenance'
        : 'available'
    const amenities = featureText
      ? featureText.split(',').map((item: string) => item.trim()).filter(Boolean)
      : []
    const body = {
      unit_type:        typeLabel,
      room_number:      label,
      description:      featureText,
      high_season_rate: Number(formData.highSeasonRate) || 1200,
      mid_season_rate:  Number(formData.midSeasonRate)  || 1000,
      low_season_rate:  Number(formData.lowSeasonRate)  || 800,
      min_stay:         Number(formData.minStay)        || 1,
      admin_fee:        Number(formData.adminFee)       || 0,
      breakage_deposit: Number(formData.breakageDeposit)|| 0,
      capacity,
      amenities,
      status: normalizedStatus,
      color: formData.color || 'blue',
      image_urls: Array.isArray(formData.images) ? formData.images : [],
      hero_image_index: Number(formData.heroImageIndex ?? 0)
    }
    try {
      const isEdit = selectedItem && selectedItem.id && !isNaN(Number(selectedItem.id))
      const url    = isEdit ? `${getApiUrl()}/api/rooms/${selectedItem.id}` : `${getApiUrl()}/api/rooms`
      const method = isEdit ? 'PATCH' : 'POST'
      const res  = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(body) })
      const json = await res.json()
      if (json.success) {
        await fetchUnits()
        closeModal()
        alert(isEdit ? 'Unit updated successfully!' : 'Unit added successfully!')
      } else {
        alert(json.error || 'Failed to save unit')
      }
    } catch (err) {
      console.error('saveUnitToApi error:', err)
      alert('Failed to save unit. Please try again.')
    }
  }

  const deleteUnitFromApi = async (unitId: string) => {
    try {
      const res  = await fetch(`${getApiUrl()}/api/rooms/${unitId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
      const json = await res.json()
      if (json.success) {
        await fetchUnits()
        closeModal()
        alert('Unit deleted successfully!')
      } else {
        alert(json.error || 'Failed to delete unit')
      }
    } catch (err) {
      console.error('deleteUnitFromApi error:', err)
      alert('Failed to delete unit. Please try again.')
    }
  }

  // Clear all data functions
  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      updateUnitsInStore([])
      setUsers([])
      dispatch(updateBookings([]))
      alert('All data has been cleared successfully!')
    }
  }

  const clearUnits = () => {
    if (window.confirm('Are you sure you want to clear all units?')) {
      void (async () => {
        try {
          await Promise.all(units.map((unit) => fetch(`${getApiUrl()}/api/rooms/${unit.id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
          })))
          await fetchUnits()
          alert('All units cleared!')
        } catch (error) {
          console.error('Failed to clear units:', error)
          alert('Failed to clear all units.')
        }
      })()
    }
  }

  // const clearUsers = () => {
  //   if (window.confirm('Are you sure you want to clear all users?')) {
  //     setUsers([])
  //     alert('All users cleared!')
  //   }
  // }

  const clearBookings = () => {
    if (window.confirm('Are you sure you want to clear all bookings?')) {
      dispatch(updateBookings([]))
      alert('All bookings cleared!')
    }
  }

  // Tab configuration
  const updateBasicInfo = async () => {
    if (window.confirm('Save changes to basic information?')) {
      await persistBusinessSettings(businessSettings, 'Basic information updated successfully.')
    }
  }

  const resetBasicInfo = async () => {
    if (window.confirm('Reset basic information to defaults?')) {
      await persistBusinessSettings(defaultBusinessSettings, 'Basic information reset to defaults.')
    }
  }

  // Homepage settings functions
  const saveHomepageSettings = async () => {
    if (!window.confirm('Save changes to homepage settings?')) {
      return
    }

    setHomepageSettingsSaving(true)
    setHomepageSettingsMessage('')

    try {
      const response = await fetch(`${getApiUrl()}/api/homepage-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(homepageSettings),
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to save homepage settings')
      }

      dispatch(setHomepageSettings(result?.data || homepageSettings))
      setHomepageSettingsMessage('Homepage settings saved successfully.')
    } catch (error: any) {
      setHomepageSettingsMessage(error?.message || 'Failed to save homepage settings.')
    } finally {
      setHomepageSettingsSaving(false)
    }
  }

  const resetHomepageSettings = () => {
    if (window.confirm('Reset homepage settings to defaults? This will restore all original content.')) {
      dispatch(setHomepageSettings(defaultHomepageSettings))
      setHomepageSettingsMessage('Homepage settings reset in the editor. Save changes to publish the defaults.')
    }
  }

  // Tab configuration
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'units', label: 'Unit Management', icon: '🏠' },
    { id: 'bookings', label: 'Booking Management', icon: '📅' },
    { id: 'availability-conflicts', label: 'Availability Conflicts', icon: '🚨' },
      { id: 'calendar', label: 'Booking Calendar', icon: '🗓️' },
    { id: 'guests', label: 'Guest Management', icon: '👥' },
    { id: 'registered-guests', label: 'Registered Guests', icon: '🔐' },
    { id: 'seasons', label: 'Season Settings', icon: '🌡️' },
    { id: 'homepage', label: 'Homepage Settings', icon: '🏡' },
    { id: 'bookingpage', label: 'Booking Page Settings', icon: '🎨' },
    { id: 'settings', label: 'Business Settings', icon: '⚙️' }
  ]

  return (
    <div className="admin-dashboard-theme min-h-screen bg-transparent">
      {/* Header */}
      <header className="dashboard-topbar sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="rounded-2xl bg-slate-950 p-3 shadow-lg">
                <span className="text-white text-xl font-bold">🏖️</span>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Operations Center</p>
                <h1 className="text-2xl font-bold text-slate-950">
                  Marina Glen Holiday Resort
                </h1>
                <p className="text-slate-600 text-sm">Resort Management Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={clearAllData}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg shadow-md transition-all duration-200"
              >
                🗑️ Clear All Data
              </button>
              <button 
                onClick={openCurrentAdminProfileModal}
                className="flex items-center space-x-2 rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 hover:bg-white transition-all duration-200 cursor-pointer shadow-sm"
              >
                <div className="w-8 h-8 rounded-full bg-slate-950 flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{adminInitial}</span>
                </div>
                <span className="text-slate-700 font-medium">{adminDisplayName}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="dashboard-tabbar">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-1 py-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id)
                }}
                className={`flex items-center space-x-3 rounded-2xl px-5 py-3 font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-slate-950 text-white shadow-sm'
                    : 'text-slate-700 hover:bg-white/80 hover:text-slate-950'
                }`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-secondary-900">Resort Dashboard</h2>
              <div className="flex space-x-4">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{units.length}</div>
                    <div className="text-sm text-secondary-600">Total Units</div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-green-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{activeBookings.length}</div>
                    <div className="text-sm text-secondary-600">Active Bookings</div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-purple-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{visibleRegisteredGuests.length}</div>
                    <div className="text-sm text-secondary-600">Registered Users</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
                <h3 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
                  <span className="text-2xl mr-3">📊</span>
                  Overview Statistics
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-secondary-700">Total Revenue (This Month)</span>
                    <span className="font-bold text-blue-700">R {activeBookings.reduce((sum, booking) => sum + booking.totalAmount, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span className="text-secondary-700">Occupancy Rate</span>
                    <span className="font-bold text-green-700">{units.length > 0 ? Math.round((activeBookings.length / units.length) * 100) : 0}%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="text-secondary-700">Average Booking Value</span>
                    <span className="font-bold text-purple-700">R {activeBookings.length > 0 ? Math.round(activeBookings.reduce((sum, booking) => sum + booking.totalAmount, 0) / activeBookings.length).toLocaleString() : 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
                <h3 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
                  <span className="text-2xl mr-3">🚀</span>
                  Quick Actions
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => openModal('edit', { modalType: 'unit' })}
                    className="p-3 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 text-blue-700 font-medium transition-colors"
                  >
                    ➕ Add Unit
                  </button>
                  <button 
                    onClick={() => void router.push('/booking-system/booking-form?mode=create')}
                    className="p-3 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 text-green-700 font-medium transition-colors"
                  >
                    📅 New Booking
                  </button>
                  <button 
                    onClick={() => void router.push('/booking-system/admin/seasonal-calendar')}
                    className="p-3 bg-yellow-50 hover:bg-yellow-100 rounded-lg border border-yellow-200 text-yellow-700 font-medium transition-colors"
                  >
                    📆 Seasonal Calendar
                  </button>
                  <button 
                    onClick={() => openModal('edit', { modalType: 'user' })}
                    className="p-3 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 text-purple-700 font-medium transition-colors"
                  >
                    👤 Add User
                  </button>
                  <button 
                    onClick={() => setActiveTab('settings')}
                    className="p-3 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200 text-orange-700 font-medium transition-colors"
                  >
                    ⚙️ Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Units Tab */}
        {activeTab === 'units' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-secondary-900">Unit Management</h2>
              <div className="flex space-x-3">
                <button 
                  onClick={() => void fetchUnits()}
                  disabled={unitsLoading}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 shadow-md transition-all duration-200 disabled:opacity-60"
                >
                  {unitsLoading ? '⏳ Loading…' : '🔄 Refresh Units'}
                </button>
                <button 
                  onClick={() => openModal('edit', { modalType: 'unit' })}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 shadow-md transition-all duration-200"
                >
                  ➕ Add New Unit
                </button>
                <button 
                  onClick={clearUnits}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 shadow-md transition-all duration-200"
                >
                  🗑️ Clear All Units
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Unit Overview</h3>
              
              {unitsLoading ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">⏳</div>
                  <h3 className="text-xl font-semibold text-secondary-700 mb-2">Loading Units</h3>
                  <p className="text-secondary-600">Fetching saved unit numbers, pricing, and images.</p>
                </div>
              ) : units.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🏠</div>
                  <h3 className="text-xl font-semibold text-secondary-700 mb-2">No Units Available</h3>
                  <p className="text-secondary-600 mb-6">Get started by adding your first unit to the system.</p>
                  <button 
                    onClick={() => openModal('edit', { modalType: 'unit' })}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 shadow-md transition-all duration-200"
                  >
                    ➕ Add Your First Unit
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {units.map((unit) => (
                    <div key={unit.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                      {/* Hero Image */}
                      {unit.images && unit.images.length > 0 && (
                        <div className="h-48 bg-gray-200 relative overflow-hidden">
                          <img 
                            src={unit.images[unit.heroImageIndex || 0]}
                            alt={`${unit.number} - Hero Image`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                          {unit.images.length > 1 && (
                            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                              +{unit.images.length - 1} more
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="p-4">
                        <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{unit.number}</h4>
                          <p className="text-sm text-gray-600">{unit.type}</p>
                          <p className="text-sm text-gray-500 mt-1">{unit.features}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            Linked units: {unit.roomNumbers && unit.roomNumbers.length > 0 ? unit.roomNumbers.join(', ') : 'No linked unit numbers'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Availability: {unit.availableRooms ?? 0} of {unit.totalRooms ?? 0} linked units available
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          unit.status === 'Available' ? 'bg-green-100 text-green-800' :
                          unit.status === 'Occupied' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {unit.status}
                        </span>
                      </div>
                      <div className="mb-3 space-y-1">
                        <div className="text-sm font-medium text-gray-700">Seasonal rates</div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="rounded bg-green-50 px-2 py-2 text-center text-green-800">
                            <div className="font-semibold">Low</div>
                            <div>R{unit.lowSeasonRate.toLocaleString()}</div>
                          </div>
                          <div className="rounded bg-yellow-50 px-2 py-2 text-center text-yellow-800">
                            <div className="font-semibold">Mid</div>
                            <div>R{unit.midSeasonRate.toLocaleString()}</div>
                          </div>
                          <div className="rounded bg-red-50 px-2 py-2 text-center text-red-800">
                            <div className="font-semibold">High</div>
                            <div>R{unit.highSeasonRate.toLocaleString()}</div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 font-normal">
                          {unit.seasonRates ? `${unit.seasonRates.length} season rates` : 'No season rates'}
                        </div>
                      </div>
                      <div className="flex flex-col space-y-2">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => openModal('view', { ...unit, modalType: 'unit' })}
                            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-3 rounded text-sm transition-colors"
                          >
                            👁️ View
                          </button>
                          <button 
                            onClick={() => openModal('edit', { ...unit, modalType: 'unit' })}
                            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded text-sm transition-colors"
                          >
                            ✏️ Edit
                          </button>
                          <button 
                            onClick={() => openModal('delete', { ...unit, modalType: 'unit' })}
                            className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-3 rounded text-sm transition-colors"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                        <button 
                          onClick={() => openModal('edit', { ...unit, modalType: 'unitSeasonRates' })}
                          className="w-full bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded text-sm transition-colors"
                        >
                          💰 Manage Season Rates
                        </button>
                      </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-secondary-900">Booking Management</h2>
              <div className="flex space-x-3">
                <button
                  onClick={() => void fetchBookings()}
                  disabled={bookingsLoading}
                  className="bg-blue-600 text-white px-5 py-3 rounded-lg hover:bg-blue-700 shadow-md transition-all duration-200 disabled:opacity-60"
                >
                  {bookingsLoading ? '⏳ Loading…' : '🔄 Refresh'}
                </button>
                <button
                  onClick={() => void router.push('/booking-system/booking-form?mode=create')}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 shadow-md transition-all duration-200"
                >
                  ➕ New Booking
                </button>
                <button
                  onClick={clearBookings}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 shadow-md transition-all duration-200"
                >
                  🗑️ Clear All Bookings
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Current Bookings</h3>
              {bookingsLoading && visibleBookings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">⏳ Loading bookings…</div>
              ) : visibleBookings.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📅</div>
                  <h3 className="text-xl font-semibold text-secondary-700 mb-2">No Bookings Found</h3>
                  <p className="text-secondary-600 mb-6">Start managing your resort by creating your first booking.</p>
                  <button
                    onClick={() => void router.push('/booking-system/booking-form?mode=create')}
                    className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 shadow-md transition-all duration-200"
                  >
                    ➕ Create First Booking
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Booking ID</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Guest</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Unit</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Dates</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Amount</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">
                          Actions
                          <span className="text-xs font-normal text-gray-500 block">📋 = Full Details</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleBookings.map((booking) => (
                        <tr key={booking.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-blue-600">{booking.id}</td>
                          <td className="py-3 px-4">
                            <div>
                              <div className="font-medium text-secondary-900">{booking.guestName}</div>
                              <div className="text-sm text-secondary-600">{booking.guestEmail}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <div className="font-medium text-secondary-900">{booking.unitNumber}</div>
                              <div className="text-sm text-secondary-600">{booking.unitType}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-sm">
                              <div>{booking.checkIn} to {booking.checkOut}</div>
                              <div className="text-secondary-600">{booking.guests} guest{Number(booking.guests) !== 1 ? 's' : ''}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4 font-bold text-green-600">R{booking.totalAmount.toLocaleString()}</td>
                          <td className="py-3 px-4">
                            <select
                              value={booking.status}
                              onChange={(e) => {
                                const newStatus = e.target.value
                                dispatch(updateBookingStatus({ id: booking.id, status: newStatus }))
                                void fetch(`${getApiUrl()}/api/bookings/${encodeURIComponent(booking.id)}`, {
                                  method: 'PATCH',
                                  headers: getAuthHeaders(),
                                  body: JSON.stringify({ status: newStatus }),
                                }).catch((err) => console.error('Failed to persist status:', err))
                              }}
                              className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${
                                booking.status === 'Confirmed' ? 'bg-green-100 text-green-800' :
                                booking.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                booking.status === 'Checked In' ? 'bg-blue-100 text-blue-800' :
                                booking.status === 'Checked Out' ? 'bg-purple-100 text-purple-800' :
                                booking.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}
                            >
                              <option value="Pending">Pending</option>
                              <option value="Confirmed">Confirmed</option>
                              <option value="Checked In">Checked In</option>
                              <option value="Checked Out">Checked Out</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex space-x-1">
                              <button
                                onClick={() => void openBookingPreview(booking.id)}
                                className="text-green-600 hover:text-green-800 p-1 rounded"
                                title="View Full Details"
                              >
                                📋
                              </button>
                              <button
                                onClick={() => openModal('view', { ...booking, modalType: 'booking' })}
                                className="text-gray-600 hover:text-gray-800 p-1 rounded"
                                title="Quick View"
                              >
                                👁️
                              </button>
                              <button
                                onClick={() => void router.push(`/booking-system/booking-form?mode=edit&id=${booking.id}`)}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                title="Edit"
                              >
                                ✏️
                              </button>
                              {booking.status === 'Pending' && (
                                <button
                                  onClick={() => {
                                    dispatch(updateBookingStatus({ id: booking.id, status: 'Confirmed' }))
                                    void fetch(`${getApiUrl()}/api/bookings/${encodeURIComponent(booking.id)}`, {
                                      method: 'PATCH',
                                      headers: getAuthHeaders(),
                                      body: JSON.stringify({ status: 'Confirmed' }),
                                    }).catch((err) => console.error('Failed to persist status:', err))
                                  }}
                                  className="text-green-600 hover:text-green-800 p-1 rounded"
                                  title="Quick Confirm"
                                >
                                  ✅
                                </button>
                              )}
                              {booking.status === 'Confirmed' && (
                                <button
                                  onClick={() => {
                                    dispatch(updateBookingStatus({ id: booking.id, status: 'Checked In' }))
                                    void fetch(`${getApiUrl()}/api/bookings/${encodeURIComponent(booking.id)}`, {
                                      method: 'PATCH',
                                      headers: getAuthHeaders(),
                                      body: JSON.stringify({ status: 'Checked In' }),
                                    }).catch((err) => console.error('Failed to persist status:', err))
                                  }}
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                  title="Quick Check In"
                                >
                                  🏠
                                </button>
                              )}
                              {booking.status === 'Checked In' && (
                                <button
                                  onClick={() => {
                                    dispatch(updateBookingStatus({ id: booking.id, status: 'Checked Out' }))
                                    void fetch(`${getApiUrl()}/api/bookings/${encodeURIComponent(booking.id)}`, {
                                      method: 'PATCH',
                                      headers: getAuthHeaders(),
                                      body: JSON.stringify({ status: 'Checked Out' }),
                                    }).catch((err) => console.error('Failed to persist status:', err))
                                  }}
                                  className="text-purple-600 hover:text-purple-800 p-1 rounded"
                                  title="Quick Check Out"
                                >
                                  🎯
                                </button>
                              )}
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Delete booking ${booking.id}? This cannot be undone.`)) return
                                  try {
                                    const res = await fetch(`${getApiUrl()}/api/bookings/${encodeURIComponent(booking.id)}`, {
                                      method: 'DELETE',
                                      headers: getAuthHeaders(),
                                    })
                                    if (res.ok) {
                                      dispatch(deleteBooking(booking.id))
                                    } else {
                                      const err = await res.json().catch(() => ({}))
                                      alert(`Failed to delete: ${err.error ?? res.statusText}`)
                                    }
                                  } catch (err) {
                                    console.error('Delete booking error:', err)
                                    alert('Failed to delete booking. Check connection.')
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 p-1 rounded"
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Booking Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-secondary-900">Booking Calendar</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    void fetchCalendarRoomUnits()
                    void fetchRoomLockouts()
                    void fetchBookings()
                  }}
                  className="bg-white text-secondary-700 px-5 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
                >
                  🔄 Refresh Calendar Data
                </button>
                <button
                  onClick={() => setActiveTab('bookings')}
                  className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                >
                  📋 View All Bookings
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setBookingCalendarView('calendar')}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${bookingCalendarView === 'calendar' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-secondary-700 border border-gray-200 hover:bg-gray-50'}`}
              >
                Calendar View
              </button>
              <button
                onClick={() => setBookingCalendarView('lockouts')}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${bookingCalendarView === 'lockouts' ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-secondary-700 border border-gray-200 hover:bg-gray-50'}`}
              >
                Lockout Dates
              </button>
            </div>

            {bookingCalendarView === 'calendar' ? (
              <BookingCalendar bookings={visibleBookings} />
            ) : (
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-secondary-900">Set Unit Lockout Dates</h3>
                    <p className="mt-1 text-sm text-secondary-600">Choose a specific unit and block it from new bookings for maintenance, owner use, or any other operational reason.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    <div className="xl:col-span-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Select Unit</label>
                      <select
                        value={roomLockoutForm.roomId}
                        onChange={(e) => setRoomLockoutForm({ ...roomLockoutForm, roomId: e.target.value })}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                      >
                        <option value="">Choose a unit...</option>
                        {calendarRoomUnits.map((unit) => (
                          <option key={unit.id} value={unit.id}>
                            {unit.roomNumber} · {unit.roomType} · {unit.status}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={roomLockoutForm.startDate}
                        onChange={(e) => setRoomLockoutForm({ ...roomLockoutForm, startDate: e.target.value })}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={roomLockoutForm.endDate}
                        onChange={(e) => setRoomLockoutForm({ ...roomLockoutForm, endDate: e.target.value })}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">Reason</label>
                    <textarea
                      value={roomLockoutForm.reason}
                      onChange={(e) => setRoomLockoutForm({ ...roomLockoutForm, reason: e.target.value })}
                      rows={3}
                      placeholder="Maintenance, owner stay, deep cleaning, refurbishment..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                    />
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="text-sm text-secondary-600">
                      Lockouts prevent the selected unit from being allocated to new bookings during the selected date range.
                    </div>
                    <button
                      onClick={() => void handleCreateRoomLockout()}
                      disabled={roomLockoutsSaving || calendarRoomUnitsLoading}
                      className="bg-slate-900 text-white px-5 py-3 rounded-lg hover:bg-slate-800 shadow-md transition-all duration-200 disabled:opacity-60"
                    >
                      {roomLockoutsSaving ? 'Saving Lockout…' : 'Save Lockout'}
                    </button>
                  </div>
                </div>

                {roomLockoutsMessage && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    {roomLockoutsMessage}
                  </div>
                )}

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4 gap-4">
                    <div>
                      <h3 className="text-xl font-semibold text-secondary-900">Scheduled Lockouts</h3>
                      <p className="mt-1 text-sm text-secondary-600">Review and remove lockout periods for specific units.</p>
                    </div>
                    <div className="text-sm text-secondary-600">{roomLockouts.length} lockout period{roomLockouts.length === 1 ? '' : 's'}</div>
                  </div>

                  {roomLockoutsLoading ? (
                    <div className="text-center py-12 text-secondary-600">Loading room lockouts…</div>
                  ) : roomLockouts.length === 0 ? (
                    <div className="text-center py-12 text-secondary-600">No lockout dates have been created yet.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 font-semibold text-secondary-700">Unit</th>
                            <th className="text-left py-3 px-4 font-semibold text-secondary-700">Date Range</th>
                            <th className="text-left py-3 px-4 font-semibold text-secondary-700">Reason</th>
                            <th className="text-left py-3 px-4 font-semibold text-secondary-700">Created</th>
                            <th className="text-left py-3 px-4 font-semibold text-secondary-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roomLockouts.map((lockout) => (
                            <tr key={lockout.id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                              <td className="py-4 px-4">
                                <div className="font-medium text-secondary-900">{lockout.roomNumber}</div>
                                <div className="text-sm text-secondary-600 mt-1">{lockout.roomType}</div>
                              </td>
                              <td className="py-4 px-4 text-sm text-secondary-700">
                                <div>{new Date(lockout.startDate).toLocaleDateString()}</div>
                                <div className="mt-1 text-secondary-500">to {new Date(lockout.endDate).toLocaleDateString()}</div>
                              </td>
                              <td className="py-4 px-4 text-sm text-secondary-700">{lockout.reason || 'No reason provided'}</td>
                              <td className="py-4 px-4 text-sm text-secondary-700">
                                {lockout.createdAt ? new Date(lockout.createdAt).toLocaleDateString() : 'Unknown'}
                              </td>
                              <td className="py-4 px-4">
                                <button
                                  onClick={() => void handleDeleteRoomLockout(lockout)}
                                  className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-red-700 transition-colors"
                                >
                                  Remove Lockout
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Guests Tab */}
        {activeTab === 'guests' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-secondary-900">Guest Management</h2>
              <div className="flex space-x-3">
                <button
                  onClick={() => void fetchBookings()}
                  disabled={bookingsLoading}
                  className="bg-blue-600 text-white px-5 py-3 rounded-lg hover:bg-blue-700 shadow-md transition-all duration-200 disabled:opacity-60"
                >
                  {bookingsLoading ? '⏳ Loading…' : '🔄 Refresh'}
                </button>
                <button
                  onClick={() => openModal('edit', { modalType: 'guest' })}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 shadow-md transition-all duration-200"
                >
                  ➕ Add Guest
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to clear all guests?')) {
                      dispatch(updateGuests([]))
                      alert('All guests cleared!')
                    }
                  }}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 shadow-md transition-all duration-200"
                >
                  🗑️ Clear All Guests
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Guest List</h3>
              {reduxGuests.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👥</div>
                  <h3 className="text-xl font-semibold text-secondary-700 mb-2">No Guests Found</h3>
                  <p className="text-secondary-600 mb-6">Guests will be automatically created when bookings are made.</p>
                  <button
                    onClick={() => openModal('edit', { modalType: 'guest' })}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 shadow-md transition-all duration-200"
                  >
                    ➕ Add Guest Manually
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Guest</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Contact</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Bookings</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Total Spent</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Last Visit</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reduxGuests.map((guest) => (
                        <tr key={guest.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <div className="font-medium text-secondary-900">{guest.name}</div>
                              <div className="text-sm text-secondary-600">ID: {guest.id}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <div className="text-sm text-secondary-900">{guest.email}</div>
                              <div className="text-sm text-secondary-600">{guest.phone}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-center">
                              <div className="text-lg font-bold text-blue-600">{guest.totalBookings}</div>
                              <div className="text-xs text-secondary-600">bookings</div>
                            </div>
                          </td>
                          <td className="py-3 px-4 font-bold text-green-600">R{guest.totalSpent.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-secondary-700">
                            {guest.lastVisit ? new Date(guest.lastVisit).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              guest.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {guest.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex space-x-1">
                              <button
                                onClick={() => openModal('view', { ...guest, modalType: 'guest' })}
                                className="text-gray-600 hover:text-gray-800 p-1 rounded"
                                title="View"
                              >
                                👁️
                              </button>
                              <button
                                onClick={() => openModal('edit', { ...guest, modalType: 'guest' })}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => openModal('delete', { ...guest, modalType: 'guest' })}
                                className="text-red-600 hover:text-red-800 p-1 rounded"
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'registered-guests' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-secondary-900">Registered Guest Accounts</h2>
                <p className="mt-2 text-secondary-600">View guest login accounts, issue password resets, and remove guest profiles while preserving booking history.</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowRegisteredGuestMarketingPanel((value) => !value)}
                  className="bg-emerald-600 text-white px-5 py-3 rounded-lg hover:bg-emerald-700 shadow-md transition-all duration-200"
                >
                  {showRegisteredGuestMarketingPanel ? '✖ Close Marketing Email' : '📣 Marketing Email'}
                </button>
                <button
                  onClick={() => void fetchRegisteredGuests()}
                  disabled={registeredGuestsLoading}
                  className="bg-blue-600 text-white px-5 py-3 rounded-lg hover:bg-blue-700 shadow-md transition-all duration-200 disabled:opacity-60"
                >
                  {registeredGuestsLoading ? '⏳ Loading…' : '🔄 Refresh Accounts'}
                </button>
              </div>
            </div>

            {showRegisteredGuestMarketingPanel && (
              <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-6 space-y-4">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-secondary-900">Send Marketing Email</h3>
                    <p className="mt-1 text-sm text-secondary-600">
                      Compose a message for {visibleRegisteredGuests.length} registered guest account{visibleRegisteredGuests.length === 1 ? '' : 's'}. Each guest receives an individual email.
                    </p>
                  </div>
                  <div className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 border border-emerald-200">
                    Audience: {visibleRegisteredGuests.length}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Email Subject</label>
                  <input
                    type="text"
                    value={registeredGuestMarketingForm.subject}
                    onChange={(e) => setRegisteredGuestMarketingForm({ ...registeredGuestMarketingForm, subject: e.target.value })}
                    placeholder="Seasonal offer, loyalty update, promotion..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Email Message</label>
                  <textarea
                    value={registeredGuestMarketingForm.message}
                    onChange={(e) => setRegisteredGuestMarketingForm({ ...registeredGuestMarketingForm, message: e.target.value })}
                    rows={8}
                    placeholder="Write the message to send to your registered guests. Line breaks will be preserved in the email."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Attachment</label>
                  <div className="flex flex-col gap-3 rounded-lg border border-dashed border-emerald-300 bg-emerald-50/40 p-4">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div className="text-sm text-secondary-600">
                        Attach one PDF, Word, Excel, or text document up to 5 MB.
                      </div>
                      <label className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-white px-4 py-2 text-sm font-medium text-emerald-700 border border-emerald-300 hover:bg-emerald-50 transition-colors">
                        {registeredGuestMarketingAttachmentLoading ? 'Preparing attachment…' : 'Choose Document'}
                        <input
                          type="file"
                          accept=".pdf,.txt,.doc,.docx,.xls,.xlsx,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          onChange={(event) => void handleRegisteredGuestMarketingAttachmentChange(event)}
                          disabled={registeredGuestMarketingAttachmentLoading || registeredGuestMarketingSending}
                          className="hidden"
                        />
                      </label>
                    </div>

                    {registeredGuestMarketingAttachment && (
                      <div className="flex flex-col gap-3 rounded-lg bg-white p-3 border border-emerald-200 md:flex-row md:items-center md:justify-between">
                        <div>
                          <div className="font-medium text-secondary-900">{registeredGuestMarketingAttachment.fileName}</div>
                          <div className="text-sm text-secondary-600">
                            {(registeredGuestMarketingAttachment.sizeBytes / 1024 / 1024).toFixed(2)} MB
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setRegisteredGuestMarketingAttachment(null)}
                          className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 transition-colors"
                        >
                          Remove Attachment
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm text-secondary-600">
                    This sends to the registered guest accounts currently listed in this panel, includes the optional attachment, and records the send summary in the admin audit log.
                  </div>
                  <button
                    onClick={() => void handleSendRegisteredGuestMarketingEmail()}
                    disabled={registeredGuestMarketingSending || registeredGuestMarketingAttachmentLoading || visibleRegisteredGuests.length === 0}
                    className="bg-emerald-600 text-white px-5 py-3 rounded-lg hover:bg-emerald-700 shadow-md transition-all duration-200 disabled:opacity-60"
                  >
                    {registeredGuestMarketingSending ? '⏳ Sending…' : 'Send Marketing Email'}
                  </button>
                </div>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
              Removing a guest profile deletes the login account from the <strong>users</strong> table and detaches guest records from that account. Existing bookings remain intact for reporting and operations.
            </div>

            {registeredGuestsMessage && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                {registeredGuestsMessage}
              </div>
            )}

            {resetPasswordInfo && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-900">
                <div className="font-semibold">Temporary password generated for {resetPasswordInfo.email}</div>
                <div className="mt-2">Share this securely with the guest:</div>
                <div className="mt-2 inline-block rounded-lg bg-white px-3 py-2 font-mono text-base text-slate-900 border border-emerald-200">
                  {resetPasswordInfo.temporaryPassword}
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-secondary-900">Account List</h3>
                <div className="text-sm text-secondary-600">{visibleRegisteredGuests.length} registered guest account{visibleRegisteredGuests.length === 1 ? '' : 's'}</div>
              </div>

              {registeredGuestsLoading ? (
                <div className="text-center py-12 text-secondary-600">Loading registered guest accounts…</div>
              ) : visibleRegisteredGuests.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔐</div>
                  <h3 className="text-xl font-semibold text-secondary-700 mb-2">No Registered Guest Accounts</h3>
                  <p className="text-secondary-600">Guest accounts will appear here once users register through the booking system.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Guest</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Contact</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Registered</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Profile</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Bookings</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRegisteredGuests.map((guest) => (
                        <tr key={guest.id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                          <td className="py-4 px-4">
                            <div className="font-medium text-secondary-900">{guest.firstName} {guest.lastName}</div>
                            <div className="text-xs text-secondary-500 mt-1">ID: {guest.id}</div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm text-secondary-900">{guest.email}</div>
                            <div className="text-sm text-secondary-600">{guest.phone || 'No phone number'}</div>
                          </td>
                          <td className="py-4 px-4 text-sm text-secondary-700">
                            {guest.createdAt ? new Date(guest.createdAt).toLocaleDateString() : 'Unknown'}
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              guest.hasGuestProfile ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {guest.hasGuestProfile ? 'Linked profile' : 'Account only'}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-sm text-secondary-700">
                            <div className="font-semibold text-blue-700">{guest.totalBookings} booking{guest.totalBookings === 1 ? '' : 's'}</div>
                            <div className="mt-1 text-secondary-600">Spent: R{guest.totalSpent.toLocaleString()}</div>
                            <div className="mt-1 text-secondary-500">Last visit: {guest.lastVisit ? new Date(guest.lastVisit).toLocaleDateString() : 'Never'}</div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => void handleResetRegisteredGuestPassword(guest)}
                                className="bg-slate-900 text-white px-3 py-2 rounded-lg text-sm hover:bg-slate-800 transition-colors"
                              >
                                Reset Password
                              </button>
                              <button
                                onClick={() => void handleDeleteRegisteredGuest(guest)}
                                className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-red-700 transition-colors"
                              >
                                Delete Profile
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'availability-conflicts' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-secondary-900">Availability Conflicts</h2>
                <p className="mt-2 text-secondary-600">Review race-condition booking failures and identify cases that may need payment follow-up or manual guest outreach.</p>
              </div>
              <button
                onClick={() => void fetchAvailabilityConflicts()}
                disabled={availabilityConflictsLoading}
                className="bg-blue-600 text-white px-5 py-3 rounded-lg hover:bg-blue-700 shadow-md transition-all duration-200 disabled:opacity-60"
              >
                {availabilityConflictsLoading ? '⏳ Loading…' : '🔄 Refresh Conflicts'}
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
              These records are written when the final backend availability lock rejects a stale or concurrent booking attempt. Entries marked <strong>follow-up required</strong> usually indicate a payment succeeded before stock could be assigned.
            </div>

            {availabilityConflictsMessage && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                {availabilityConflictsMessage}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-red-200 p-5">
                <div className="text-sm font-medium text-secondary-600">Logged conflicts</div>
                <div className="mt-2 text-3xl font-bold text-red-600">{availabilityConflicts.length}</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-5">
                <div className="text-sm font-medium text-secondary-600">Need follow-up</div>
                <div className="mt-2 text-3xl font-bold text-amber-600">
                  {availabilityConflicts.filter((item) => item.followUpRequired).length}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-5">
                <div className="text-sm font-medium text-secondary-600">Blocked before payment follow-up</div>
                <div className="mt-2 text-3xl font-bold text-emerald-600">
                  {availabilityConflicts.filter((item) => !item.followUpRequired).length}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-secondary-900">Conflict Log</h3>
                <div className="text-sm text-secondary-600">{availabilityConflicts.length} conflict record{availabilityConflicts.length === 1 ? '' : 's'}</div>
              </div>

              {availabilityConflictsLoading ? (
                <div className="text-center py-12 text-secondary-600">Loading availability conflicts…</div>
              ) : availabilityConflicts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">✅</div>
                  <h3 className="text-xl font-semibold text-secondary-700 mb-2">No Availability Conflicts Logged</h3>
                  <p className="text-secondary-600">When concurrent booking attempts collide, the audit trail will appear here for staff review.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Logged</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Booking</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Guest</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Stay</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Unit</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Source</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availabilityConflicts.map((conflict) => {
                        const loggedAt = conflict.loggedAt || conflict.changedAt
                        const stayLabel = conflict.checkIn && conflict.checkOut
                          ? `${new Date(conflict.checkIn).toLocaleDateString()} → ${new Date(conflict.checkOut).toLocaleDateString()}`
                          : 'Dates unavailable'

                        return (
                          <tr key={conflict.id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                            <td className="py-4 px-4 text-sm text-secondary-700 whitespace-nowrap">
                              {loggedAt ? new Date(loggedAt).toLocaleString() : 'Unknown'}
                            </td>
                            <td className="py-4 px-4">
                              <div className="font-medium text-secondary-900">{conflict.bookingReference || 'No reference'}</div>
                              <div className="mt-1 text-xs text-secondary-500">Payment: {conflict.paymentReference || 'Not captured'}</div>
                              {conflict.totalAmount != null && (
                                <div className="mt-1 text-xs text-secondary-500">Amount: R{conflict.totalAmount.toLocaleString()}</div>
                              )}
                            </td>
                            <td className="py-4 px-4 text-sm text-secondary-700">
                              {conflict.guestEmail || 'No guest email'}
                            </td>
                            <td className="py-4 px-4 text-sm text-secondary-700 whitespace-nowrap">
                              {stayLabel}
                            </td>
                            <td className="py-4 px-4 text-sm text-secondary-700">
                              <div>{conflict.roomType || `Room type #${conflict.roomTypeId ?? 'Unknown'}`}</div>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                conflict.source === 'yoco_webhook'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-slate-100 text-slate-800'
                              }`}>
                                {conflict.source === 'yoco_webhook' ? 'Payment confirmation' : 'Booking creation'}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                conflict.followUpRequired
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-emerald-100 text-emerald-800'
                              }`}>
                                {conflict.followUpRequired ? 'Follow-up required' : 'Guest blocked before payment'}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Season Settings Tab */}
        {activeTab === 'seasons' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-secondary-900">Season Settings</h2>
              <button
                onClick={() => void fetchSeasons()}
                disabled={seasonsLoading}
                className="bg-blue-600 text-white px-5 py-3 rounded-lg hover:bg-blue-700 shadow-md transition-all duration-200 disabled:opacity-60"
              >
                {seasonsLoading ? '⏳ Loading…' : '🔄 Refresh'}
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
              <strong>How it works:</strong> Define date ranges for each season. All unit types share these periods — the rates per season are set individually on each unit in Unit Management.
              Any date not covered by a period defaults to <strong>Mid Season</strong> pricing.
            </div>

            {/* Add Season Form */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">➕ Add Season Period</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={seasonForm.start_date}
                    onChange={(e) => setSeasonForm(f => ({ ...f, start_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={seasonForm.end_date}
                    onChange={(e) => setSeasonForm(f => ({ ...f, end_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Season Type</label>
                  <select
                    value={seasonForm.season}
                    onChange={(e) => setSeasonForm(f => ({ ...f, season: e.target.value as 'high' | 'mid' | 'low' }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="high">🔴 High Season</option>
                    <option value="mid">🟡 Mid Season</option>
                    <option value="low">🟢 Low Season</option>
                  </select>
                </div>
                <div>
                  <button
                    onClick={() => void handleAddSeason()}
                    disabled={seasonSaving}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 font-medium"
                  >
                    {seasonSaving ? '⏳ Saving…' : '💾 Add Period'}
                  </button>
                </div>
              </div>
              {seasonFormError && (
                <p className="mt-3 text-sm text-red-600 font-medium">{seasonFormError}</p>
              )}
            </div>

            {/* Season Periods List */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">📋 Season Periods</h3>
              {seasonsLoading && globalSeasons.length === 0 ? (
                <div className="text-center py-10 text-gray-500">⏳ Loading seasons…</div>
              ) : globalSeasons.length === 0 ? (
                <div className="text-center py-10">
                  <div className="text-5xl mb-3">🗓️</div>
                  <p className="text-secondary-600">No season periods defined yet. Add one above.</p>
                  <p className="text-sm text-secondary-500 mt-1">All dates will default to Mid Season pricing until periods are configured.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Season</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Start Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">End Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Duration</th>
                        <th className="text-left py-3 px-4 font-semibold text-secondary-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {globalSeasons
                        .slice()
                        .sort((a, b) => a.startDate.localeCompare(b.startDate))
                        .map((period) => {
                          const start = new Date(period.startDate)
                          const end = new Date(period.endDate)
                          const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24)) + 1
                          const seasonLabel = period.season === 'high' ? '🔴 High Season' : period.season === 'low' ? '🟢 Low Season' : '🟡 Mid Season'
                          const seasonBadge = period.season === 'high'
                            ? 'bg-red-100 text-red-800'
                            : period.season === 'low'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                          return (
                            <tr key={period.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${seasonBadge}`}>
                                  {seasonLabel}
                                </span>
                              </td>
                              <td className="py-3 px-4 font-medium">{period.startDate}</td>
                              <td className="py-3 px-4 font-medium">{period.endDate}</td>
                              <td className="py-3 px-4 text-secondary-600">{days} day{days !== 1 ? 's' : ''}</td>
                              <td className="py-3 px-4">
                                <button
                                  onClick={() => void handleDeleteSeason(period.id)}
                                  className="text-red-600 hover:text-red-800 p-1 rounded"
                                  title="Delete"
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Season Legend */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">💡 Season Rate Guide</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: 'High Season', icon: '🔴', desc: 'Peak demand period (school holidays, public holidays). Highest nightly rate applies.', color: 'border-red-200 bg-red-50' },
                  { label: 'Mid Season', icon: '🟡', desc: 'Moderate demand. Mid-season nightly rate applies. Dates without a period default here.', color: 'border-yellow-200 bg-yellow-50' },
                  { label: 'Low Season', icon: '🟢', desc: 'Off-peak period. Lowest nightly rate applies to attract bookings.', color: 'border-green-200 bg-green-50' },
                ].map((s) => (
                  <div key={s.label} className={`rounded-lg border p-4 ${s.color}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{s.icon}</span>
                      <span className="font-semibold text-secondary-900">{s.label}</span>
                    </div>
                    <p className="text-sm text-secondary-600">{s.desc}</p>
                    <p className="text-xs text-secondary-500 mt-2">Rates set per unit in Unit Management →</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-secondary-900">Business Settings</h2>
            
            {/* Basic Information */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-secondary-900">Basic Information</h3>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => void updateBasicInfo()}
                    disabled={businessSettingsLoading || businessSettingsSaving}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {businessSettingsSaving ? 'Saving...' : '💾 Update'}
                  </button>
                  <button 
                    onClick={() => void resetBasicInfo()}
                    disabled={businessSettingsLoading || businessSettingsSaving}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    🔄 Reset
                  </button>
                </div>
              </div>

              {businessSettingsMessage && (
                <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-sm">
                  {businessSettingsMessage}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Resort Name</label>
                  <input 
                    type="text" 
                    value={businessSettings.name}
                    onChange={(e) => setBusinessSettings({...businessSettings, name: e.target.value})}
                    disabled={businessSettingsLoading || businessSettingsSaving}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Phone Number</label>
                  <input 
                    type="text" 
                    value={businessSettings.phone}
                    onChange={(e) => setBusinessSettings({...businessSettings, phone: e.target.value})}
                    disabled={businessSettingsLoading || businessSettingsSaving}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Email Address</label>
                  <input 
                    type="email" 
                    value={businessSettings.email}
                    onChange={(e) => setBusinessSettings({...businessSettings, email: e.target.value})}
                    disabled={businessSettingsLoading || businessSettingsSaving}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Website</label>
                  <input 
                    type="url" 
                    value={businessSettings.website}
                    onChange={(e) => setBusinessSettings({...businessSettings, website: e.target.value})}
                    disabled={businessSettingsLoading || businessSettingsSaving}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Address</label>
                  <input 
                    type="text" 
                    value={businessSettings.address}
                    onChange={(e) => setBusinessSettings({...businessSettings, address: e.target.value})}
                    disabled={businessSettingsLoading || businessSettingsSaving}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Description</label>
                  <textarea 
                    value={businessSettings.description}
                    onChange={(e) => setBusinessSettings({...businessSettings, description: e.target.value})}
                    rows={4}
                    disabled={businessSettingsLoading || businessSettingsSaving}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-secondary-900">Admin Access Credentials</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => void loadAdminAuthSettings()}
                    disabled={adminAuthLoading}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    {adminAuthLoading ? 'Loading...' : 'Reload'}
                  </button>
                </div>
              </div>

              {adminAuthMessage && (
                <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-sm">
                  {adminAuthMessage}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Signed-In Admin Email</label>
                  <input
                    type="email"
                    value={adminAuthSettings.adminEmail}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-secondary-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Authentication Provider</label>
                  <input
                    type="text"
                    value={adminAuthSettings.provider}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-secondary-700 uppercase"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Credential Storage</label>
                  <input
                    type="text"
                    value={adminAuthSettings.credentialStorage}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-secondary-700"
                  />
                </div>
              </div>
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                Admin credentials are no longer stored or changed in this dashboard. Access is controlled through Supabase Auth and the linked admin user record.
              </div>
            </div>
          </div>
        )}

        {/* Homepage Settings Tab */}
        {activeTab === 'homepage' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-secondary-900">Homepage Settings</h2>
              <div className="flex space-x-2">
                <button 
                  onClick={saveHomepageSettings}
                  disabled={homepageSettingsSaving}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {homepageSettingsSaving ? 'Saving...' : '💾 Save Changes'}
                </button>
                <button 
                  onClick={resetHomepageSettings}
                  disabled={homepageSettingsSaving}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
                >
                  🔄 Reset to Defaults
                </button>
              </div>
            </div>

            {homepageSettingsMessage && (
              <div className={`rounded-lg border px-4 py-3 text-sm ${homepageSettingsMessage.includes('successfully') || homepageSettingsMessage.includes('reset') ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
                {homepageSettingsMessage}
              </div>
            )}
            
            {/* Hero Section */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Hero Section</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hero Title</label>
                  <input 
                    type="text" 
                    value={homepageSettings.heroTitle}
                    onChange={(e) => dispatch(updateHomepageSettings({ heroTitle: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hero Subtitle</label>
                  <input 
                    type="text" 
                    value={homepageSettings.heroSubtitle}
                    onChange={(e) => dispatch(updateHomepageSettings({ heroSubtitle: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hero Description</label>
                  <textarea 
                    value={homepageSettings.heroDescription}
                    onChange={(e) => dispatch(updateHomepageSettings({ heroDescription: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Features Section</h3>
              <div className="grid grid-cols-1 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Features Title</label>
                  <input 
                    type="text" 
                    value={homepageSettings.featuresTitle}
                    onChange={(e) => dispatch(updateHomepageSettings({ featuresTitle: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Features Subtitle</label>
                  <textarea 
                    value={homepageSettings.featuresSubtitle}
                    onChange={(e) => dispatch(updateHomepageSettings({ featuresSubtitle: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              {/* Feature Cards */}
              <h4 className="text-lg font-semibold text-secondary-900 mb-3">Feature Cards</h4>
              <div className="space-y-4">
                {homepageSettings.featureCards.map((card) => (
                  <div key={card.id} className="p-4 border border-gray-200 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <span className="text-2xl">{card.emoji}</span>
                      {card.title}
                    </h5>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input 
                          type="text" 
                          value={card.title}
                          onChange={(e) => dispatch(updateFeatureCard({ 
                            ...card, 
                            title: e.target.value 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Emoji</label>
                        <input 
                          type="text" 
                          value={card.emoji}
                          onChange={(e) => dispatch(updateFeatureCard({ 
                            ...card, 
                            emoji: e.target.value 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="🌊"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Feature Image</label>
                        <div className="space-y-2">
                          <input 
                            type="text" 
                            placeholder="Enter image URL or upload below"
                            value={card.image || ''}
                            onChange={(e) => dispatch(updateFeatureCard({ 
                              ...card, 
                              image: e.target.value 
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <input 
                            type="file" 
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                const url = URL.createObjectURL(file)
                                dispatch(updateFeatureCard({ 
                                  ...card, 
                                  image: url 
                                }))
                              }
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          {card.image && (
                            <div className="mt-2">
                              <img 
                                src={card.image} 
                                alt={`${card.title} preview`}
                                className="w-full h-24 object-cover rounded-md border border-gray-200"
                                onError={(e) => {
                                  e.currentTarget.src = getPlaceholderImageUrl()
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="lg:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea 
                          value={card.description}
                          onChange={(e) => dispatch(updateFeatureCard({ 
                            ...card, 
                            description: e.target.value 
                          }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Accommodation Section */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Accommodation Section</h3>
              <div className="grid grid-cols-1 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Accommodation Title</label>
                  <input 
                    type="text" 
                    value={homepageSettings.accommodationTitle}
                    onChange={(e) => dispatch(updateHomepageSettings({ accommodationTitle: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Accommodation Subtitle</label>
                  <textarea 
                    value={homepageSettings.accommodationSubtitle}
                    onChange={(e) => dispatch(updateHomepageSettings({ accommodationSubtitle: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              {/* Unit Descriptions */}
              <h4 className="text-lg font-semibold text-secondary-900 mb-3">Unit Descriptions</h4>
              <div className="space-y-4">
                {homepageSettings.unitDescriptions
                  .filter((unit) => unit.unitType !== 'Bigger 6 sleeper Unit')
                  .map((unit) => (
                  <div key={unit.unitType} className="p-4 border border-gray-200 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-3">{unit.unitType}</h5>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Display Title</label>
                        <input 
                          type="text" 
                          value={unit.title}
                          onChange={(e) => dispatch(updateUnitDescription({ 
                            ...unit, 
                            title: e.target.value 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea 
                          value={unit.description}
                          onChange={(e) => dispatch(updateUnitDescription({ 
                            ...unit, 
                            description: e.target.value 
                          }))}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit Image</label>
                        <div className="space-y-2">
                          <input 
                            type="text" 
                            placeholder="Enter image URL or upload below"
                            value={unit.image || ''}
                            onChange={(e) => dispatch(updateUnitDescription({ 
                              ...unit, 
                              image: e.target.value 
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <input 
                            type="file" 
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                const url = URL.createObjectURL(file)
                                dispatch(updateUnitDescription({ 
                                  ...unit, 
                                  image: url 
                                }))
                              }
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          {unit.image && (
                            <div className="mt-2">
                              <img 
                                src={unit.image} 
                                alt={`${unit.unitType} preview`}
                                className="w-full h-32 object-cover rounded-md border border-gray-200"
                                onError={(e) => {
                                  e.currentTarget.src = getPlaceholderImageUrl()
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Call-to-Action Section</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CTA Title</label>
                  <input 
                    type="text" 
                    value={homepageSettings.ctaTitle}
                    onChange={(e) => dispatch(updateHomepageSettings({ ctaTitle: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CTA Description</label>
                  <textarea 
                    value={homepageSettings.ctaDescription}
                    onChange={(e) => dispatch(updateHomepageSettings({ ctaDescription: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Booking Page Settings Tab */}
        {activeTab === 'bookingpage' && (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-secondary-900">Booking Page Settings</h2>
            
            {/* General Settings */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-secondary-900">General Settings</h3>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => {
                      if (window.confirm('Save booking page settings?')) {
                        void persistBookingPageSettings(bookingPageSettings, 'Booking page settings updated successfully.')
                      }
                    }}
                    disabled={bookingPageSettingsLoading || bookingPageSettingsSaving}
                    data-testid="save-booking-page-settings"
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {bookingPageSettingsSaving ? 'Saving...' : '💾 Update'}
                  </button>
                  <button 
                    onClick={() => {
                      if (window.confirm('Reset booking page settings to defaults?')) {
                        void persistBookingPageSettings(defaultBookingPageSettings, 'Booking page settings reset to defaults.')
                      }
                    }}
                    disabled={bookingPageSettingsLoading || bookingPageSettingsSaving}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    🔄 Reset
                  </button>
                </div>
              </div>

              {bookingPageSettingsMessage && (
                <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-sm">
                  {bookingPageSettingsMessage}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Page Title</label>
                  <input 
                    type="text" 
                    value={bookingPageSettings.title}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, title: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Page Subtitle</label>
                  <input 
                    type="text" 
                    value={bookingPageSettings.subtitle}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, subtitle: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Contact Email</label>
                  <input 
                    type="email" 
                    value={bookingPageSettings.contactEmail}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, contactEmail: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Contact Phone</label>
                  <input 
                    type="tel" 
                    value={bookingPageSettings.contactPhone}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, contactPhone: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Check-in Time</label>
                  <input 
                    type="time" 
                    value={bookingPageSettings.checkInTime}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, checkInTime: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Check-out Time</label>
                  <input 
                    type="time" 
                    value={bookingPageSettings.checkOutTime}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, checkOutTime: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Welcome Message</label>
                  <textarea 
                    value={bookingPageSettings.welcomeMessage}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, welcomeMessage: e.target.value})}
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Cancellation Policy</label>
                  <textarea 
                    value={bookingPageSettings.cancellationPolicy}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, cancellationPolicy: e.target.value})}
                    rows={2}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Footer Text</label>
                  <input 
                    type="text" 
                    value={bookingPageSettings.footerText}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, footerText: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Color Customization */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Color Customization</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Primary Color</label>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="color" 
                      value={bookingPageSettings.primaryColor}
                      onChange={(e) => setBookingPageSettings({...bookingPageSettings, primaryColor: e.target.value})}
                      className="w-12 h-12 border border-gray-300 rounded cursor-pointer"
                    />
                    <input 
                      type="text" 
                      value={bookingPageSettings.primaryColor}
                      onChange={(e) => setBookingPageSettings({...bookingPageSettings, primaryColor: e.target.value})}
                      className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Accent Color</label>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="color" 
                      value={bookingPageSettings.accentColor}
                      onChange={(e) => setBookingPageSettings({...bookingPageSettings, accentColor: e.target.value})}
                      className="w-12 h-12 border border-gray-300 rounded cursor-pointer"
                    />
                    <input 
                      type="text" 
                      value={bookingPageSettings.accentColor}
                      onChange={(e) => setBookingPageSettings({...bookingPageSettings, accentColor: e.target.value})}
                      className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Background Color</label>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="color" 
                      value={bookingPageSettings.backgroundColor}
                      onChange={(e) => setBookingPageSettings({...bookingPageSettings, backgroundColor: e.target.value})}
                      className="w-12 h-12 border border-gray-300 rounded cursor-pointer"
                    />
                    <input 
                      type="text" 
                      value={bookingPageSettings.backgroundColor}
                      onChange={(e) => setBookingPageSettings({...bookingPageSettings, backgroundColor: e.target.value})}
                      className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Button Color</label>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="color" 
                      value={bookingPageSettings.buttonColor}
                      onChange={(e) => setBookingPageSettings({...bookingPageSettings, buttonColor: e.target.value})}
                      className="w-12 h-12 border border-gray-300 rounded cursor-pointer"
                    />
                    <input 
                      type="text" 
                      value={bookingPageSettings.buttonColor}
                      onChange={(e) => setBookingPageSettings({...bookingPageSettings, buttonColor: e.target.value})}
                      className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Gateway Settings */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-secondary-900">Payment Gateway Settings</h3>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    secureSaveAvailable
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-amber-100 text-amber-700 border border-amber-200'
                  }`}>
                    {secureSaveAvailable ? 'Supabase secure save available' : 'Secure save unavailable'}
                  </span>
                  <button
                    onClick={() => void loadPaymentGatewaySettings()}
                    disabled={paymentSettingsLoading || paymentSettingsSaving}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    {paymentSettingsLoading ? 'Loading...' : 'Reload'}
                  </button>
                  <button
                    onClick={() => void savePaymentGatewaySettings()}
                    disabled={paymentSettingsLoading || paymentSettingsSaving}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {paymentSettingsSaving ? 'Saving...' : 'Save Payment Settings'}
                  </button>
                </div>
              </div>

              {paymentSettingsMessage && (
                <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-sm">
                  {paymentSettingsMessage}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">YOCO Public Key</label>
                  <input
                    type="text"
                    value={paymentGatewaySettings.yocoPublicKey}
                    onChange={(e) => setPaymentGatewaySettings({ ...paymentGatewaySettings, yocoPublicKey: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">YOCO Secret Key</label>
                  <input
                    type="password"
                    value={paymentGatewaySettings.yocoSecretKey}
                    onChange={(e) => setPaymentGatewaySettings({ ...paymentGatewaySettings, yocoSecretKey: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">YOCO Webhook Secret</label>
                  <input
                    type="password"
                    value={paymentGatewaySettings.yocoWebhookSecret}
                    onChange={(e) => setPaymentGatewaySettings({ ...paymentGatewaySettings, yocoWebhookSecret: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">EFT Bank Name</label>
                  <input
                    type="text"
                    value={paymentGatewaySettings.eftBankName}
                    onChange={(e) => setPaymentGatewaySettings({ ...paymentGatewaySettings, eftBankName: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">EFT Account Name</label>
                  <input
                    type="text"
                    value={paymentGatewaySettings.eftAccountName}
                    onChange={(e) => setPaymentGatewaySettings({ ...paymentGatewaySettings, eftAccountName: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">EFT Account Number</label>
                  <input
                    type="text"
                    value={paymentGatewaySettings.eftAccountNumber}
                    onChange={(e) => setPaymentGatewaySettings({ ...paymentGatewaySettings, eftAccountNumber: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">EFT Branch Code</label>
                  <input
                    type="text"
                    value={paymentGatewaySettings.eftBranchCode}
                    onChange={(e) => setPaymentGatewaySettings({ ...paymentGatewaySettings, eftBranchCode: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">EFT Account Type</label>
                  <input
                    type="text"
                    value={paymentGatewaySettings.eftAccountType}
                    onChange={(e) => setPaymentGatewaySettings({ ...paymentGatewaySettings, eftAccountType: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">EFT Reference Prefix</label>
                  <input
                    type="text"
                    value={paymentGatewaySettings.eftReferencePrefix}
                    onChange={(e) => setPaymentGatewaySettings({ ...paymentGatewaySettings, eftReferencePrefix: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">EFT Instructions</label>
                  <textarea
                    value={paymentGatewaySettings.eftInstructions}
                    onChange={(e) => setPaymentGatewaySettings({ ...paymentGatewaySettings, eftInstructions: e.target.value })}
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Feature Toggles */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Feature Display Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <input 
                    type="checkbox" 
                    checked={bookingPageSettings.showUnitImages}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, showUnitImages: e.target.checked})}
                    className="w-5 h-5 text-blue-600"
                  />
                  <span className="text-secondary-700">Show Unit Images</span>
                </label>
                <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <input 
                    type="checkbox" 
                    checked={bookingPageSettings.showUnitFeatures}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, showUnitFeatures: e.target.checked})}
                    className="w-5 h-5 text-blue-600"
                  />
                  <span className="text-secondary-700">Show Unit Features</span>
                </label>
                <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <input 
                    type="checkbox" 
                    checked={bookingPageSettings.showPricing}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, showPricing: e.target.checked})}
                    className="w-5 h-5 text-blue-600"
                  />
                  <span className="text-secondary-700">Show Pricing</span>
                </label>
                <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <input 
                    type="checkbox" 
                    checked={bookingPageSettings.showGuestReviews}
                    onChange={(e) => setBookingPageSettings({...bookingPageSettings, showGuestReviews: e.target.checked})}
                    data-testid="toggle-guest-reviews"
                    className="w-5 h-5 text-blue-600"
                  />
                  <span className="text-secondary-700">Show Guest Reviews</span>
                </label>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-secondary-900">Guest Feedback Moderation</h3>
                  <p className="mt-2 text-sm text-secondary-600">
                    Website feedback submissions and longer booking or enquiry messages can be reviewed here before they appear on the public booking page.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => void loadGuestFeedbackModeration()}
                    disabled={guestFeedbackLoading || guestFeedbackSaving}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    {guestFeedbackLoading ? 'Loading...' : 'Reload Feedback'}
                  </button>
                  <button
                    onClick={() => void persistGuestFeedbackModeration()}
                    disabled={guestFeedbackLoading || guestFeedbackSaving}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {guestFeedbackSaving ? 'Saving...' : 'Save Moderation'}
                  </button>
                </div>
              </div>

              {guestFeedbackMessage && (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                  {guestFeedbackMessage}
                </div>
              )}

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Candidates</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900">{guestFeedbackItems.length}</div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Approved</div>
                  <div className="mt-2 text-2xl font-bold text-emerald-800">{guestFeedbackItems.filter((item) => item.isApproved).length}</div>
                </div>
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">Featured</div>
                  <div className="mt-2 text-2xl font-bold text-sky-800">{guestFeedbackItems.filter((item) => item.isApproved && item.isFeatured).length}</div>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                {guestFeedbackItems.length > 0 ? guestFeedbackItems.map((item) => (
                  <div key={item.id} className="rounded-xl border border-slate-200 p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="max-w-3xl">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                            {item.sourceLabel}
                          </span>
                          <span className="text-sm font-medium text-secondary-700">{item.guestName}</span>
                          {item.guestContact && <span className="text-sm text-secondary-500">{item.guestContact}</span>}
                          <span className="text-sm text-secondary-500">{item.location}</span>
                          <span className="text-sm text-secondary-500">{new Date(item.submittedAt).toLocaleDateString()}</span>
                        </div>
                        <div className="mt-2 text-sm font-semibold text-secondary-900">{item.stayLabel}</div>
                        <p className="mt-3 text-sm leading-6 text-secondary-700">{item.quote}</p>
                      </div>

                      <div className="grid gap-3 sm:min-w-[14rem]">
                        <label className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 px-4 py-3 text-sm font-medium text-secondary-700">
                          <span>Approved for public use</span>
                          <input
                            type="checkbox"
                            checked={item.isApproved}
                            onChange={(e) => updateGuestFeedbackItem(item.id, 'isApproved', e.target.checked)}
                            className="h-5 w-5 text-blue-600"
                          />
                        </label>
                        <label className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 px-4 py-3 text-sm font-medium text-secondary-700">
                          <span>Featured on booking page</span>
                          <input
                            type="checkbox"
                            checked={item.isApproved && item.isFeatured}
                            onChange={(e) => updateGuestFeedbackItem(item.id, 'isFeatured', e.target.checked)}
                            className="h-5 w-5 text-blue-600"
                          />
                        </label>
                        <button
                          onClick={() => void removeGuestFeedbackItem(item)}
                          disabled={guestFeedbackLoading || guestFeedbackSaving}
                          className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Remove Feedback
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-secondary-600">
                    No website feedback submissions, booking notes, or enquiry messages are currently available for moderation.
                  </div>
                )}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Booking Page Preview</h3>
              <div className="bg-gray-100 rounded-lg p-6" style={{backgroundColor: bookingPageSettings.backgroundColor}}>
                <div className="bg-white rounded-lg shadow-sm p-6 max-w-2xl mx-auto" style={{backgroundColor: bookingPageSettings.headerBackgroundColor}}>
                  <h1 className="text-3xl font-bold mb-2" style={{color: bookingPageSettings.primaryColor}}>
                    {bookingPageSettings.title}
                  </h1>
                  <p className="text-lg mb-6" style={{color: bookingPageSettings.textColor}}>
                    {bookingPageSettings.subtitle}
                  </p>
                  <div className="space-y-4">
                    <div className="text-sm" style={{color: bookingPageSettings.textColor}}>
                      <strong>Check-in:</strong> {bookingPageSettings.checkInTime} | <strong>Check-out:</strong> {bookingPageSettings.checkOutTime}
                    </div>
                    <div className="text-sm" style={{color: bookingPageSettings.textColor}}>
                      <strong>Contact:</strong> {bookingPageSettings.contactEmail} | {bookingPageSettings.contactPhone}
                    </div>
                    <button 
                      className="px-6 py-3 rounded-lg text-white font-medium"
                      style={{backgroundColor: bookingPageSettings.buttonColor, color: bookingPageSettings.buttonTextColor}}
                    >
                      Book Now
                    </button>
                    <div className="text-sm border-t pt-4" style={{color: bookingPageSettings.textColor}}>
                      {bookingPageSettings.welcomeMessage}
                    </div>
                    <div className="text-xs text-center pt-4" style={{color: bookingPageSettings.textColor}}>
                      {bookingPageSettings.footerText}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-secondary-900">
                  {modalType === 'unitSeasonRates' 
                    ? `Season Rates - ${selectedItem?.number || 'Unit'}`
                    : isCurrentAdminProfileModal
                      ? 'Edit Profile'
                    : `${modalMode === 'view' ? 'View' : modalMode === 'edit' ? 'Edit' : 'Delete'} ${modalType}`
                  }
                </h3>
                <button 
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {modalMode === 'delete' ? (
                <div className="text-center">
                  <div className="text-6xl mb-4">⚠️</div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-2">Confirm Deletion</h4>
                  <p className="text-secondary-600 mb-6">Are you sure you want to delete this {modalType}? This action cannot be undone.</p>
                  <div className="flex space-x-3">
                    <button 
                      onClick={closeModal}
                      className="flex-1 bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => {
                        if (modalType === 'unit' && selectedItem) {
                          void deleteUnitFromApi(selectedItem.id)
                          return
                        }
                        if (modalType === 'user' && selectedItem) {
                          setUsers(users.filter(user => user.id !== selectedItem.id))
                        } else if (modalType === 'guest' && selectedItem) {
                          dispatch(deleteGuest(selectedItem.id))
                        } else if (modalType === 'booking' && selectedItem) {
                          dispatch(deleteBooking(selectedItem.id))
                        } else if (modalType === 'unitSeasonRates' && selectedItem) {
                          const updatedUnit = { ...selectedItem, seasonRates: [] }
                          setUnits(units.map(unit => unit.id === selectedItem.id ? updatedUnit : unit))
                        }
                        closeModal()
                        alert(`${modalType} deleted successfully!`)
                      }}
                      className="flex-1 bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-secondary-600">
                    {modalType === 'unitSeasonRates' 
                      ? `Manage season-specific pricing for ${selectedItem?.number || 'this unit'}`
                      : isCurrentAdminProfileModal
                        ? 'Update your admin profile details.'
                      : modalMode === 'view' 
                        ? 'Viewing details for this ' 
                        : 'Edit the details for this '
                    }{modalType !== 'unitSeasonRates' ? modalType : ''}.
                  </p>
                  
                  {/* Basic form fields based on type */}
                  {modalType === 'unit' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Unit Number</label>
                        <input 
                          type="text" 
                          value={formData.number || ''}
                          onChange={(e) => setFormData({...formData, number: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="e.g., Unit 101"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Unit Type</label>
                        <select 
                          value={formData.type || ''}
                          onChange={(e) => setFormData({...formData, type: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        >
                          <option value="">Select unit type</option>
                          <option value="4-Sleeper Unit">4-Sleeper Unit</option>
                          <option value="6-Sleeper Unit">6-Sleeper Unit</option>
                          <option value="Bigger 6-Sleeper Unit">Bigger 6-Sleeper Unit</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Features</label>
                        <input 
                          type="text" 
                          value={formData.features || ''}
                          onChange={(e) => setFormData({...formData, features: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="e.g., Ocean View, Full Kitchen"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Seasonal Rates</label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <input 
                            type="number" 
                            value={formData.lowSeasonRate || ''}
                            onChange={(e) => setFormData({...formData, lowSeasonRate: e.target.value ? parseInt(e.target.value) : 0})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            placeholder="Low season"
                          />
                          <input 
                            type="number" 
                            value={formData.midSeasonRate || ''}
                            onChange={(e) => setFormData({...formData, midSeasonRate: e.target.value ? parseInt(e.target.value) : 0})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            placeholder="Mid season"
                          />
                          <input 
                            type="number" 
                            value={formData.highSeasonRate || ''}
                            onChange={(e) => setFormData({...formData, highSeasonRate: e.target.value ? parseInt(e.target.value) : 0})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            placeholder="High season"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Minimum Stay</label>
                          <input 
                            type="number" 
                            value={formData.minStay || ''}
                            onChange={(e) => setFormData({...formData, minStay: e.target.value ? parseInt(e.target.value) : 1})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            placeholder="2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Admin Fee</label>
                          <input 
                            type="number" 
                            value={formData.adminFee || ''}
                            onChange={(e) => setFormData({...formData, adminFee: e.target.value ? parseInt(e.target.value) : 0})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            placeholder="200"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Breakage Deposit</label>
                          <input 
                            type="number" 
                            value={formData.breakageDeposit || ''}
                            onChange={(e) => setFormData({...formData, breakageDeposit: e.target.value ? parseInt(e.target.value) : 0})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            placeholder="500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Status</label>
                        <select 
                          value={formData.status || ''}
                          onChange={(e) => setFormData({...formData, status: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        >
                          <option value="">Select status</option>
                          <option value="Available">Available</option>
                          <option value="Occupied">Occupied</option>
                          <option value="Maintenance">Maintenance</option>
                        </select>
                      </div>
                      
                      {/* Images Section */}
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Unit Images</label>
                        <div className="space-y-3">
                          {/* Current Images Display */}
                          {formData.images && formData.images.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {formData.images.map((imageUrl: string, index: number) => (
                                <div key={index} className="relative group">
                                  <img 
                                    src={imageUrl} 
                                    alt={`Unit ${formData.number} - Image ${index + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border border-gray-300"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.src = '/placeholder-image.jpg';
                                    }}
                                  />
                                  {/* Hero Badge */}
                                  {formData.heroImageIndex === index && (
                                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                                      Hero
                                    </div>
                                  )}
                                  {/* Action Buttons */}
                                  {modalMode !== 'view' && (
                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <div className="flex space-x-1">
                                        <button
                                          onClick={() => {
                                            setFormData({...formData, heroImageIndex: index})
                                          }}
                                          className="bg-blue-600 text-white p-1 rounded text-xs hover:bg-blue-700"
                                          title="Set as Hero Image"
                                        >
                                          ⭐
                                        </button>
                                        <button
                                          onClick={() => {
                                            const newImages = formData.images.filter((_: string, i: number) => i !== index)
                                            const newHeroIndex = formData.heroImageIndex === index ? 0 : 
                                              formData.heroImageIndex > index ? formData.heroImageIndex - 1 : formData.heroImageIndex
                                            setFormData({
                                              ...formData, 
                                              images: newImages,
                                              heroImageIndex: newImages.length > 0 ? newHeroIndex : undefined
                                            })
                                          }}
                                          className="bg-red-600 text-white p-1 rounded text-xs hover:bg-red-700"
                                          title="Remove Image"
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Add New Images */}
                          {modalMode !== 'view' && (
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                              <div className="text-center">
                                <div className="text-4xl mb-2">📷</div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Add Unit Images</h4>
                                <p className="text-xs text-gray-500 mb-3">Add multiple images for this unit. The first image or selected hero image will be the main display image.</p>
                                
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        const input = e.target as HTMLInputElement;
                                        const imageUrl = input.value.trim();
                                        if (imageUrl) {
                                          const currentImages = formData.images || [];
                                          const newImages = [...currentImages, imageUrl];
                                          setFormData({
                                            ...formData, 
                                            images: newImages,
                                            heroImageIndex: formData.heroImageIndex !== undefined ? formData.heroImageIndex : 0
                                          });
                                          input.value = '';
                                        }
                                      }
                                    }}
                                  />
                                  <p className="text-xs text-gray-400">Press Enter to add the image</p>
                                </div>
                                
                                {/* File Upload Section */}
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <p className="text-sm font-medium text-gray-900 mb-2">Or upload from your computer:</p>
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="file"
                                      accept="image/*,.jpg,.jpeg,.png,.gif,.webp"
                                      multiple
                                      onChange={(e) => {
                                        const files = Array.from(e.target.files || []);
                                        const label = document.querySelector('label[for="image-upload"]');
                                        if (files.length > 0) {
                                          if (label) {
                                            label.innerHTML = '<span>⏳</span><span>Processing...</span>';
                                          }
                                          
                                          let processedCount = 0;
                                          files.forEach(file => {
                                            if (file.type.startsWith('image/')) {
                                              const reader = new FileReader();
                                              reader.onload = (event) => {
                                                const imageDataUrl = event.target?.result as string;
                                                if (imageDataUrl) {
                                                  const currentImages = formData.images || [];
                                                  const newImages = [...currentImages, imageDataUrl];
                                                  setFormData({
                                                    ...formData, 
                                                    images: newImages,
                                                    heroImageIndex: formData.heroImageIndex !== undefined ? formData.heroImageIndex : 0
                                                  });
                                                }
                                                processedCount++;
                                                // Reset label when done
                                                if (processedCount === files.length) {
                                                  setTimeout(() => {
                                                    if (label) {
                                                      label.innerHTML = '<span>📁</span><span>Choose Files</span>';
                                                    }
                                                  }, 500);
                                                }
                                              };
                                              reader.readAsDataURL(file);
                                            }
                                          });
                                        }
                                        // Clear the input
                                        e.target.value = '';
                                      }}
                                      className="hidden"
                                      id="image-upload"
                                    />
                                    <label 
                                      htmlFor="image-upload"
                                      className="cursor-pointer bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                                    >
                                      <span>📁</span>
                                      <span>Choose Files</span>
                                    </label>
                                    <span className="text-xs text-gray-500">JPG, PNG, GIF, WebP</span>
                                  </div>
                                  <p className="text-xs text-gray-400 mt-1">Select multiple images to upload them all at once</p>
                                </div>
                                
                                <div className="mt-3 text-xs text-gray-500">
                                  <p><strong>Sample URLs you can use:</strong></p>
                                  <div className="space-y-1 mt-1">
                                    <p>• https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&h=600&fit=crop</p>
                                    <p>• https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=800&h=600&fit=crop</p>
                                    <p>• https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {modalType === 'user' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">First Name</label>
                        <input 
                          type="text" 
                          value={formData.firstName || ''}
                          onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Last Name</label>
                        <input 
                          type="text" 
                          value={formData.lastName || ''}
                          onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Email</label>
                        <input 
                          type="email" 
                          value={formData.email || ''}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          disabled={modalMode === 'view' || isCurrentAdminProfileModal}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Role</label>
                        <select 
                          value={formData.role || ''}
                          onChange={(e) => setFormData({...formData, role: e.target.value})}
                          disabled={modalMode === 'view' || isCurrentAdminProfileModal}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        >
                          <option value="">Select role</option>
                          <option value="admin">Admin</option>
                          <option value="staff">Staff</option>
                          <option value="guest">Guest</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {modalType === 'guest' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Guest Name</label>
                        <input 
                          type="text" 
                          value={formData.name || ''}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="Full guest name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Email</label>
                        <input 
                          type="email" 
                          value={formData.email || ''}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="guest@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Phone</label>
                        <input 
                          type="tel" 
                          value={formData.phone || ''}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="+27 XX XXX XXXX"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Total Bookings</label>
                          <input 
                            type="number" 
                            value={formData.totalBookings || 0}
                            onChange={(e) => setFormData({...formData, totalBookings: e.target.value ? parseInt(e.target.value, 10) : 0})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Total Spent</label>
                          <input 
                            type="number" 
                            value={formData.totalSpent || 0}
                            onChange={(e) => setFormData({...formData, totalSpent: e.target.value ? parseInt(e.target.value, 10) : 0})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Last Visit</label>
                          <input 
                            type="date" 
                            value={formData.lastVisit ? String(formData.lastVisit).split('T')[0] : ''}
                            onChange={(e) => setFormData({...formData, lastVisit: e.target.value})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Status</label>
                          <select 
                            value={formData.status || 'Active'}
                            onChange={(e) => setFormData({...formData, status: e.target.value})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Preferences</label>
                        <textarea 
                          value={formData.preferences || ''}
                          onChange={(e) => setFormData({...formData, preferences: e.target.value})}
                          disabled={modalMode === 'view'}
                          rows={3}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="Guest preferences, special requirements, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Notes</label>
                        <textarea 
                          value={formData.notes || ''}
                          onChange={(e) => setFormData({...formData, notes: e.target.value})}
                          disabled={modalMode === 'view'}
                          rows={3}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="Internal notes about this guest"
                        />
                      </div>
                      {modalMode === 'view' && (
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-sm text-blue-900">
                          <div><strong>Guest ID:</strong> {formData.id || 'Not assigned'}</div>
                          <div><strong>Created:</strong> {formData.createdAt ? new Date(formData.createdAt).toLocaleString() : 'Unknown'}</div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {modalType === 'booking' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Guest Name</label>
                        <input 
                          type="text" 
                          value={formData.guestName || ''}
                          onChange={(e) => setFormData({...formData, guestName: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Guest Email</label>
                        <input 
                          type="email" 
                          value={formData.guestEmail || ''}
                          onChange={(e) => setFormData({...formData, guestEmail: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="guest@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Guest Phone</label>
                        <input 
                          type="tel" 
                          value={formData.guestPhone || ''}
                          onChange={(e) => setFormData({...formData, guestPhone: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="+27 XX XXX XXXX"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Check In</label>
                          <input 
                            type="date" 
                            value={formData.checkIn || ''}
                            onChange={(e) => setFormData({...formData, checkIn: e.target.value})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Check Out</label>
                          <input 
                            type="date" 
                            value={formData.checkOut || ''}
                            onChange={(e) => setFormData({...formData, checkOut: e.target.value})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Unit Number</label>
                        <input 
                          type="text" 
                          value={formData.unitNumber || ''}
                          onChange={(e) => setFormData({...formData, unitNumber: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="e.g., Unit 23"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Unit Type</label>
                        <select 
                          value={formData.unitType || ''}
                          onChange={(e) => setFormData({...formData, unitType: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        >
                          <option value="">Select unit type</option>
                          <option value="4-Sleeper Unit">4-Sleeper Unit</option>
                          <option value="6-Sleeper Unit">6-Sleeper Unit</option>
                          <option value="Bigger 6-Sleeper Unit">Bigger 6-Sleeper Unit</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Total Amount</label>
                        <input 
                          type="number" 
                          value={formData.totalAmount || ''}
                          onChange={(e) => setFormData({...formData, totalAmount: parseInt(e.target.value)})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="Total amount"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Status</label>
                          <select 
                            value={formData.status || 'Pending'}
                            onChange={(e) => setFormData({...formData, status: e.target.value})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          >
                            <option value="Pending">Pending</option>
                            <option value="Confirmed">Confirmed</option>
                            <option value="Checked In">Checked In</option>
                            <option value="Checked Out">Checked Out</option>
                            <option value="Cancelled">Cancelled</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">Payment Status</label>
                          <select 
                            value={formData.payment || 'Pending'}
                            onChange={(e) => setFormData({...formData, payment: e.target.value})}
                            disabled={modalMode === 'view'}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          >
                            <option value="Pending">Pending</option>
                            <option value="Deposit Paid">Deposit Paid</option>
                            <option value="Paid">Paid</option>
                            <option value="Refunded">Refunded</option>
                            <option value="Cancelled">Cancelled</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Guest Count</label>
                        <input 
                          type="text" 
                          value={formData.guests || ''}
                          onChange={(e) => setFormData({...formData, guests: e.target.value})}
                          disabled={modalMode === 'view'}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="e.g., 4 adults, 2 children"
                        />
                      </div>
                    </div>
                  )}
                  
                  {modalType === 'unitSeasonRates' && (
                    <div className="space-y-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-2">Season Rates for {selectedItem?.number}</h4>
                        <p className="text-blue-700 text-sm">
                          Manage different pricing for various seasons throughout the year for this specific unit.
                        </p>
                      </div>
                      
                      {/* Current Season Rates */}
                      <div>
                        <div className="flex justify-between items-center mb-4">
                          <h5 className="font-medium text-gray-900">Current Season Rates</h5>
                          <button 
                            onClick={() => {
                              // Add new season rate to this unit
                              const newSeasonRate: SeasonRate = {
                                id: Date.now().toString(),
                                seasonName: 'New Season',
                                dateRange: 'Select dates',
                                highSeasonRate: selectedItem?.highSeasonRate || 1200,
                                midSeasonRate: selectedItem?.midSeasonRate || 1000,
                                lowSeasonRate: selectedItem?.lowSeasonRate || 800,
                                minStay: selectedItem?.minStay || 2,
                                adminFee: selectedItem?.adminFee || 200,
                                breakageDeposit: selectedItem?.breakageDeposit || 500
                              }
                              
                              const updatedUnit = {
                                ...selectedItem,
                                seasonRates: [...(selectedItem?.seasonRates || []), newSeasonRate]
                              }
                              
                              setUnits(units.map(unit => 
                                unit.id === selectedItem.id ? updatedUnit : unit
                              ))
                              setSelectedItem(updatedUnit)
                            }}
                            className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                          >
                            ➕ Add Season Rate
                          </button>
                        </div>
                        
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {selectedItem?.seasonRates?.length === 0 || !selectedItem?.seasonRates ? (
                            <div className="text-center py-8 text-gray-500">
                              <div className="text-4xl mb-2">📅</div>
                              <p>No season rates set for this unit</p>
                              <p className="text-sm">Add your first season rate above</p>
                            </div>
                          ) : (
                            selectedItem?.seasonRates?.map((seasonRate: SeasonRate, index: number) => (
                              <div key={seasonRate.id} className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <input 
                                      type="text"
                                      value={seasonRate.seasonName}
                                      onChange={(e) => {
                                        const updatedSeasonRates = [...(selectedItem?.seasonRates || [])]
                                        updatedSeasonRates[index] = { ...seasonRate, seasonName: e.target.value }
                                        const updatedUnit = { ...selectedItem, seasonRates: updatedSeasonRates }
                                        setUnits(units.map(unit => unit.id === selectedItem.id ? updatedUnit : unit))
                                        setSelectedItem(updatedUnit)
                                      }}
                                      className="font-medium text-gray-900 border-none bg-transparent focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                                      placeholder="Season Name"
                                    />
                                    <input 
                                      type="text"
                                      value={seasonRate.dateRange}
                                      onChange={(e) => {
                                        const updatedSeasonRates = [...(selectedItem?.seasonRates || [])]
                                        updatedSeasonRates[index] = { ...seasonRate, dateRange: e.target.value }
                                        const updatedUnit = { ...selectedItem, seasonRates: updatedSeasonRates }
                                        setUnits(units.map(unit => unit.id === selectedItem.id ? updatedUnit : unit))
                                        setSelectedItem(updatedUnit)
                                      }}
                                      className="text-sm text-gray-600 border-none bg-transparent focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 mt-1"
                                      placeholder="Date Range"
                                    />
                                  </div>
                                  <button 
                                    onClick={() => {
                                      const updatedSeasonRates = selectedItem?.seasonRates?.filter((_: SeasonRate, i: number) => i !== index) || []
                                      const updatedUnit = { ...selectedItem, seasonRates: updatedSeasonRates }
                                      setUnits(units.map(unit => unit.id === selectedItem.id ? updatedUnit : unit))
                                      setSelectedItem(updatedUnit)
                                    }}
                                    className="text-red-500 hover:text-red-700 p-1"
                                  >
                                    🗑️
                                  </button>
                                </div>
                                
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">High Season</label>
                                    <input 
                                      type="number"
                                      value={seasonRate.highSeasonRate}
                                      onChange={(e) => {
                                        const updatedSeasonRates = [...(selectedItem?.seasonRates || [])]
                                        updatedSeasonRates[index] = { ...seasonRate, highSeasonRate: parseInt(e.target.value) || 0 }
                                        const updatedUnit = { ...selectedItem, seasonRates: updatedSeasonRates }
                                        setUnits(units.map(unit => unit.id === selectedItem.id ? updatedUnit : unit))
                                        setSelectedItem(updatedUnit)
                                      }}
                                      className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Mid Season</label>
                                    <input 
                                      type="number"
                                      value={seasonRate.midSeasonRate}
                                      onChange={(e) => {
                                        const updatedSeasonRates = [...(selectedItem?.seasonRates || [])]
                                        updatedSeasonRates[index] = { ...seasonRate, midSeasonRate: parseInt(e.target.value) || 0 }
                                        const updatedUnit = { ...selectedItem, seasonRates: updatedSeasonRates }
                                        setUnits(units.map(unit => unit.id === selectedItem.id ? updatedUnit : unit))
                                        setSelectedItem(updatedUnit)
                                      }}
                                      className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Low Season</label>
                                    <input 
                                      type="number"
                                      value={seasonRate.lowSeasonRate}
                                      onChange={(e) => {
                                        const updatedSeasonRates = [...(selectedItem?.seasonRates || [])]
                                        updatedSeasonRates[index] = { ...seasonRate, lowSeasonRate: parseInt(e.target.value) || 0 }
                                        const updatedUnit = { ...selectedItem, seasonRates: updatedSeasonRates }
                                        setUnits(units.map(unit => unit.id === selectedItem.id ? updatedUnit : unit))
                                        setSelectedItem(updatedUnit)
                                      }}
                                      className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mt-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Min Stay</label>
                                    <input 
                                      type="number"
                                      value={seasonRate.minStay}
                                      onChange={(e) => {
                                        const updatedSeasonRates = [...(selectedItem?.seasonRates || [])]
                                        updatedSeasonRates[index] = { ...seasonRate, minStay: parseInt(e.target.value) || 1 }
                                        const updatedUnit = { ...selectedItem, seasonRates: updatedSeasonRates }
                                        setUnits(units.map(unit => unit.id === selectedItem.id ? updatedUnit : unit))
                                        setSelectedItem(updatedUnit)
                                      }}
                                      className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Admin Fee</label>
                                    <input 
                                      type="number"
                                      value={seasonRate.adminFee}
                                      onChange={(e) => {
                                        const updatedSeasonRates = [...(selectedItem?.seasonRates || [])]
                                        updatedSeasonRates[index] = { ...seasonRate, adminFee: parseInt(e.target.value) || 0 }
                                        const updatedUnit = { ...selectedItem, seasonRates: updatedSeasonRates }
                                        setUnits(units.map(unit => unit.id === selectedItem.id ? updatedUnit : unit))
                                        setSelectedItem(updatedUnit)
                                      }}
                                      className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Breakage Deposit</label>
                                    <input 
                                      type="number"
                                      value={seasonRate.breakageDeposit}
                                      onChange={(e) => {
                                        const updatedSeasonRates = [...(selectedItem?.seasonRates || [])]
                                        updatedSeasonRates[index] = { ...seasonRate, breakageDeposit: parseInt(e.target.value) || 0 }
                                        const updatedUnit = { ...selectedItem, seasonRates: updatedSeasonRates }
                                        setUnits(units.map(unit => unit.id === selectedItem.id ? updatedUnit : unit))
                                        setSelectedItem(updatedUnit)
                                      }}
                                      className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                      
                      {/* Close button for season rates modal */}
                      <div className="flex justify-end mt-6">
                        <button 
                          onClick={closeModal}
                          className="bg-blue-500 text-white py-2 px-6 rounded hover:bg-blue-600 transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {modalMode === 'edit' && modalType !== 'unitSeasonRates' && (
                    <div className="flex space-x-3 mt-6">
                      <button 
                        onClick={closeModal}
                        className="flex-1 bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button 
                          disabled={adminProfileSaving}
                          onClick={() => {
                            if (modalType === 'unit') {
                              void saveUnitToApi()
                              return
                            } else if (modalType === 'user') {
                              if (formData.isCurrentAdminProfile) {
                                void saveCurrentAdminProfile()
                                return
                              }
                              if (selectedItem) {
                                setUsers(users.map(user =>
                                  user.id === selectedItem.id ? { ...formData, id: selectedItem.id, lastLogin: new Date().toISOString().split('T')[0] } : user
                                ))
                              } else {
                                const newUser = { ...formData, id: Date.now().toString(), status: 'Active', lastLogin: new Date().toISOString().split('T')[0] }
                                setUsers([...users, newUser])
                              }
                            } else if (modalType === 'guest') {
                              const guestRecord: Guest = {
                                id: selectedItem?.id || `GUEST-${Date.now()}`,
                                name: formData.name || '',
                                email: formData.email || '',
                                phone: formData.phone || '',
                                totalBookings: Number(formData.totalBookings) || 0,
                                totalSpent: Number(formData.totalSpent) || 0,
                                lastVisit: formData.lastVisit || '',
                                status: formData.status === 'Inactive' ? 'Inactive' : 'Active',
                                preferences: formData.preferences || '',
                                notes: formData.notes || '',
                                createdAt: selectedItem?.createdAt || new Date().toISOString(),
                              }

                              if (selectedItem?.id) {
                                dispatch(updateGuest(guestRecord))
                              } else {
                                dispatch(addGuest(guestRecord))
                              }
                            } else if (modalType === 'booking') {
                              if (selectedItem && selectedItem.id) {
                                const updatedBooking = { ...formData, id: selectedItem.id } as Booking
                                dispatch(updateBooking(updatedBooking))
                              } else {
                                const newBooking: Booking = {
                                  id: `MG-${Date.now()}`,
                                  guestName:   formData.guestName   || '',
                                  guestEmail:  formData.guestEmail  || '',
                                  guestPhone:  formData.guestPhone  || '',
                                  unitType:    formData.unitType    || '',
                                  unitNumber:  formData.unitNumber  || `Unit ${Math.floor(Math.random() * 999) + 100}`,
                                  checkIn:     formData.checkIn     || '',
                                  checkOut:    formData.checkOut    || '',
                                  guests:      formData.guests      || '2 adults',
                                  totalAmount: formData.totalAmount || 0,
                                  status:      formData.status      || 'Pending',
                                  payment:     formData.payment     || 'Pending'
                                }
                                const existingGuest = reduxGuests.find(guest => guest.email === newBooking.guestEmail)
                                if (!existingGuest && newBooking.guestEmail) {
                                  const newGuest: Guest = {
                                    id: `GUEST-${Date.now()}`,
                                    name: newBooking.guestName, email: newBooking.guestEmail,
                                    phone: newBooking.guestPhone, totalBookings: 1,
                                    totalSpent: newBooking.totalAmount, lastVisit: newBooking.checkIn,
                                    status: 'Active', preferences: '',
                                    notes: `Auto-created from booking ${newBooking.id}`,
                                    createdAt: new Date().toISOString()
                                  }
                                  dispatch(addGuest(newGuest))
                                } else if (existingGuest) {
                                  dispatch(updateGuestBookingStats({ email: newBooking.guestEmail, bookingAmount: newBooking.totalAmount, bookingDate: newBooking.checkIn }))
                                }
                                dispatch(addBooking(newBooking))
                              }
                            } else if (modalType === 'unitSeasonRates') {
                              // Season rates are saved inline in the modal interactions.
                            }
                            handleSave()
                          }}
                        className="flex-1 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors disabled:cursor-not-allowed disabled:bg-blue-300"
                      >
                        {adminProfileSaving && isCurrentAdminProfileModal ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
